import AssetNotes from '../../components/AssetNotes.jsx'
import { useWorkspaceRightTab } from '../../hooks/useWorkspaceRightTab.js'
import { useOptionalCommands } from '../../commands/CommandContext.js'
import { useAddAssetIdea } from '../../hooks/useAddAssetIdea.js'
import { useEquityPriceDisplays } from '../../hooks/useEquityPriceDisplays.js'
import { projectSetupLifecycle } from '../../lib/positionListStage.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus, Search, X } from 'lucide-react'
import WorkspaceCharts from '../../components/chart/WorkspaceCharts.jsx'
import { CHART_FRAME_CLASS } from '../../components/chart/chartTokens.js'
import PositionHeader from '../../components/workspace/PositionHeader.jsx'
import ActionPanel from '../../components/workspace/ActionPanel.jsx'
import SetupStagingPanel from '../../components/workspace/SetupStagingPanel.jsx'
import WorkingOrdersPanel from '../../components/workspace/WorkingOrdersPanel.jsx'
import WorkingSetupCard from '../../components/workspace/WorkingSetupCard.jsx'
import { useSetupEntryStatus } from '../../hooks/useSetupEntryStatus.js'
import PositionSwitcher from '../../components/workspace/PositionSwitcher.jsx'
import AssetInfoCard, { AssetInfoSplit } from '../../components/workspace/AssetInfoCard.jsx'
import { useAssetInfo } from '../../hooks/useAssetInfo.js'
import PositionWorkspaceCommandSurface from '../../components/commands/PositionWorkspaceCommandSurface.jsx'
import WorkspaceColumnKeyboardSurface from '../../components/commands/WorkspaceColumnKeyboardSurface.jsx'
import OrderConfirmationModal from '../../components/OrderConfirmationModal.jsx'
import DecisionTimeline, { NotesList } from '../../components/DecisionTimeline.jsx'
import AccountName from '../../components/AccountName.jsx'
import PrivateValue from '../../components/PrivateValue.jsx'
import { useChartExecutionHistory } from '../../hooks/useChartExecutionHistory.js'
import { useAuth } from '../../hooks/useAuth.jsx'
import { useSettings } from '../../hooks/useSettings.js'
import { SCHWAB_SYNC_COMPLETE_EVENT } from '../../hooks/useAutoSync.js'
import { useBiometricGate } from '../../hooks/useBiometricGate.js'
import { useOrderGateway } from '../../hooks/useOrderGateway.js'
import { useAccountNicknames } from '../../hooks/useAccountNicknames.jsx'
import { useNotifications } from '../../hooks/useNotifications.jsx'
import { useServerLayoutNumber } from '../../hooks/useServerLayoutNumber.js'
import { useResizableWorkspaceColumns } from '../../hooks/useResizableWorkspaceColumns.js'
import { useIbkrPositionQuoteSnapshot } from '../../hooks/useIbkrQuoteSnapshot.js'
import { getUserTimezone, formatTime } from '../../lib/timezone.js'
import { supabase } from '../../lib/supabase.js'
import { useWorkingOrders, flattenChildStrategies } from '../../hooks/useWorkingOrders.js'
import {
  buildPositionOverlayProps,
  consolidateExecutions,
} from '../../lib/chart/positionOverlayAdapter.js'
import {
  CHART_PREFERENCE_KEYS,
  readServerChartPreference,
  writeServerChartPreference,
} from '../../lib/chart/chartPreferenceStore.js'
import {
  buildStopLines,
  resolveWorkspaceProtection,
} from '../../lib/workspace/resolveWorkspaceState.js'
import SetupHistory from '../../components/workspace/SetupHistory.jsx'
import SetupEditReview from '../../components/workspace/SetupEditReview.jsx'
import { restoreSetupAsIdea, visibleSetupHistory } from '../../lib/workspace/restoreSetup.js'
import { isRetiredSetup, retireUnfilledSetup } from '../../lib/workspace/setupRetirement.js'
import { hasSetupDetails } from '../../lib/workspace/setupPresence.js'
import { isOCCSymbol } from '../../lib/occParser.js'
import {
  buildPositionWorkspaceTarget,
  POSITION_WORKSPACE_FALLBACK_PATH,
  readPositionWorkspaceSelection,
  rememberPositionWorkspaceSelection,
} from '../../lib/positionWorkspaceRoute.js'

const VALID_TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min', '65min', 'daily', 'weekly'])
const DEFAULT_TIMEFRAME = 'daily'
const IBKR_RUNTIME_TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min', 'daily', 'weekly'])
const EXTENDED_HOURS_TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min'])
const VALID_MARKET_SESSIONS = new Set(['regular', 'all'])
const DEFAULT_CHART_TYPE = 'candlestick'
const RIGHT_TABS = ['actions', 'setups', 'orders', 'executions', 'notes']
const VALID_CHART_TYPES = new Set(['candlestick', 'bar'])
const SYMBOL_PATTERN = /^[A-Z0-9.\-/]{1,21}$/
const DEMO_CHART_PREFERENCE_SCOPE = 'position'

const LIVE_SELL_LIMIT_STATUSES = new Set([
  'WORKING',
  'ACCEPTED',
  'PENDING_ACTIVATION',
  'QUEUED',
  'AWAITING_RELEASE_TIME',
])
function fmtCurrency(value) {
  if (value == null || value === '') return '-'
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function rightTabLabel(tab) {
  if (tab === 'actions') return 'Actions'
  if (tab === 'setups') return 'Setups'
  if (tab === 'orders') return 'Orders'
  if (tab === 'executions') return 'Executions'
  if (tab === 'notes') return 'Notes'
  return tab
}

function orderSymbol(order, fallback = '') {
  return String(
    order?.symbol
    || order?.orderLegCollection?.[0]?.instrument?.symbol
    || fallback
    || ''
  ).toUpperCase()
}

function orderSide(order, fallback = '') {
  return String(
    order?.side
    || order?.instruction
    || order?.orderLegCollection?.[0]?.instruction
    || fallback
    || ''
  )
}

function orderAccount(order, fallback = '') {
  return String(order?.accountNumber || order?.account_number || fallback || '').trim()
}

function orderPriceNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const direct = Number(value)
    if (Number.isFinite(direct) && direct > 0) return direct
    const match = String(value).replace(/,/g, '').match(/\d+(\.\d+)?/)
    if (match) {
      const parsed = Number(match[0])
      if (Number.isFinite(parsed) && parsed > 0) return parsed
    }
  }
  return null
}

function isBuyInstruction(side) {
  return side === 'BUY' || side === 'BUY_TO_OPEN'
}

function normalizeAccountNumbers(values) {
  return [...new Set(
    (Array.isArray(values) ? values : [])
      .map(value => (value == null ? '' : String(value).trim()))
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b))
}

function activeSellLimitPrice(order, inherited = {}) {
  const status = String(order?.status || inherited.status || '').toUpperCase()
  const orderType = String(order?.orderType || '').toUpperCase()
  const side = orderSide(order, inherited.side).toUpperCase()
  const price = orderPriceNumber(order?.price, order?.limitPrice, order?.targetPrice)
  if (
    orderType === 'LIMIT'
    && (side === 'SELL' || side === 'SELL_TO_CLOSE')
    && LIVE_SELL_LIMIT_STATUSES.has(status)
    && price > 0
  ) {
    return price
  }
  return null
}

function directChildren(order) {
  const rawChildren = order?.childOrderStrategies || order?._raw?.childOrderStrategies
  const flatRawChildren = typeof flattenChildStrategies === 'function'
    ? flattenChildStrategies(rawChildren || [])
    : (rawChildren || [])
  return [
    ...(Array.isArray(order?.groupedOrders) ? order.groupedOrders : []),
    ...(Array.isArray(order?.childOrders) ? order.childOrders : []),
    ...(Array.isArray(flatRawChildren) ? flatRawChildren : []),
  ]
}

function hasActiveSellLimitDescendant(order, inherited, seen = new Set()) {
  if (!order || seen.has(order)) return false
  seen.add(order)
  return directChildren(order).some((child) => {
    const childInherited = {
      symbol: orderSymbol(child, inherited.symbol),
      status: String(child?.status || inherited.status || '').toUpperCase(),
      side: orderSide(child, inherited.side),
      accountNumber: orderAccount(child, inherited.accountNumber),
    }
    return activeSellLimitPrice(child, childInherited) != null || hasActiveSellLimitDescendant(child, childInherited, seen)
  })
}

function collectLiveSellLimitPrices(orders, symbol, accountNumbers = []) {
  const upperSymbol = String(symbol || '').toUpperCase()
  if (!upperSymbol) return []
  const accountSet = new Set(normalizeAccountNumbers(accountNumbers))
  const prices = []

  const visit = (order, inherited = {}) => {
    if (!order) return
    const sym = orderSymbol(order, inherited.symbol)
    const accountNumber = orderAccount(order, inherited.accountNumber)
    const accountMatches = accountSet.size === 0 || accountSet.has(accountNumber)
    const status = String(order.status || inherited.status || '').toUpperCase()
    const orderType = String(order.orderType || '').toUpperCase()
    const side = orderSide(order, inherited.side).toUpperCase()
    const rawChildren = order.childOrderStrategies || order._raw?.childOrderStrategies
    const hasChildren = (Array.isArray(order.groupedOrders) && order.groupedOrders.length > 0)
      || (Array.isArray(order.childOrders) && order.childOrders.length > 0)
      || (Array.isArray(rawChildren) && rawChildren.length > 0)
    const hasLiveTargetChild = hasActiveSellLimitDescendant(order, {
      symbol: sym,
      status,
      side,
      accountNumber,
    })
    const awaitingEntryParent = inherited.awaitingEntryParent
      || (
        hasChildren
        && isBuyInstruction(side)
        && LIVE_SELL_LIMIT_STATUSES.has(status)
        && !hasLiveTargetChild
      )

    if (
      sym === upperSymbol
      && accountMatches
      && orderType === 'SPLIT_BRACKET'
      && LIVE_SELL_LIMIT_STATUSES.has(status)
      && !hasLiveTargetChild
      && orderPriceNumber(order.targetPrice) > 0
    ) {
      prices.push(orderPriceNumber(order.targetPrice))
    }

    const price = activeSellLimitPrice(order, inherited)
    if (
      sym === upperSymbol
      && accountMatches
      && !awaitingEntryParent
      && price != null
    ) {
      prices.push(price)
    }

    for (const grouped of Array.isArray(order.groupedOrders) ? order.groupedOrders : []) {
      visit(grouped, { symbol: sym, status, side, accountNumber, awaitingEntryParent })
    }
    for (const child of Array.isArray(order.childOrders) ? order.childOrders : []) {
      visit(child, { symbol: sym, status, side, accountNumber, awaitingEntryParent })
    }
    const flatRawChildren = typeof flattenChildStrategies === 'function'
      ? flattenChildStrategies(rawChildren || [])
      : (rawChildren || [])
    for (const child of flatRawChildren) {
      visit(child, { symbol: sym, status, side, accountNumber, awaitingEntryParent })
    }
  }

  for (const order of Array.isArray(orders) ? orders : []) visit(order)
  return prices
}

function compactSetupLines(project) {
  if (!project) return null
  const lines = {}
  if (project.trigger_price != null) lines.triggerPrice = Number(project.trigger_price)
  if (project.pattern_failure_price != null) lines.patternFailurePrice = Number(project.pattern_failure_price)
  if (project.sell_target_price != null) lines.sellTargetPrice = Number(project.sell_target_price)
  return Object.keys(lines).length > 0 ? lines : null
}

function isWorkspaceProject(project) {
  if (isRetiredSetup(project)) return false
  const isEmptyIdea = ['idea', 'planned'].includes(String(project?.stage || '').toLowerCase())
    && !project?.trade_id && !hasSetupDetails(project) && project?.setup_source !== 'Ideas List'
  return !isEmptyIdea
}

function pickPrimaryProject(projects) {
  const rows = Array.isArray(projects) ? projects : []
  return rows.find((p) => p?.stage === 'active')
    || rows.find((p) => p?.stage === 'working')
    || rows[0]
    || null
}

function readStoredChartType() {
  return DEFAULT_CHART_TYPE
}

function readInitialTimeframe(searchParams, fallbackTimeframe = null) {
  const param = searchParams.get('timeframe')
  if (IBKR_RUNTIME_TIMEFRAMES.has(param)) return param
  if (IBKR_RUNTIME_TIMEFRAMES.has(fallbackTimeframe)) return fallbackTimeframe
  return DEFAULT_TIMEFRAME
}

function readStoredTimelineCollapsed() {
  return false
}

function demoShellPreferencePayload({ chartType, timeframe, timelineCollapsed, marketSession }) {
  return {
    version: 1,
    chartType,
    timeframe,
    timelineCollapsed,
    marketSession: VALID_MARKET_SESSIONS.has(marketSession) ? marketSession : 'regular',
  }
}

function isMobileViewport() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(max-width: 1023px)').matches
}

function useDemoMobileLayout() {
  const [isMobile, setIsMobile] = useState(isMobileViewport)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const query = window.matchMedia('(max-width: 1023px)')
    const update = () => setIsMobile(query.matches)
    update()
    query.addEventListener?.('change', update)
    query.addListener?.(update)
    return () => {
      query.removeEventListener?.('change', update)
      query.removeListener?.(update)
    }
  }, [])

  return isMobile
}

function buildEphemeralProject(symbol, userId, fields = {}) {
  return {
    id: null,
    symbol: String(symbol || '').toUpperCase(),
    stage: 'idea',
    direction: 'LONG',
    user_id: userId || null,
    created_at: new Date().toISOString(),
    account_sizing: [],
    ...fields,
  }
}

function normalizeSearchSymbol(value) {
  return String(value || '').trim().toUpperCase()
}

function quoteFieldNumber(value) {
  if (value == null || value === '') return 0
  const direct = Number(value)
  if (Number.isFinite(direct)) return direct
  const match = String(value).match(/-?\d+(\.\d+)?/)
  return match ? Number(match[0]) : 0
}

function isEphemeralSearchResult(result) {
  return result?.type === 'verified' || result?.type === 'ephemeral'
}

function ephemeralSearchSubtitle(verification) {
  if (verification?.status === 'verified') return 'Verified symbol - ephemeral workspace'
  if (verification?.status === 'checking') return 'Opening immediately - quote checking'
  if (verification?.status === 'unverified') return 'Ephemeral workspace - quote unavailable'
  return 'Ephemeral workspace'
}

function stagePriority(stage) {
  const normalized = String(stage || '').toLowerCase()
  if (normalized === 'active') return 0
  if (normalized === 'working') return 1
  if (normalized === 'idea' || normalized === 'planned') return 2
  return 3
}

function groupSearchProjects(rows) {
  const bySymbol = new Map()
  for (const row of Array.isArray(rows) ? rows : []) {
    const symbol = normalizeSearchSymbol(row?.symbol)
    if (!symbol) continue
    const existing = bySymbol.get(symbol)
    if (!existing) {
      bySymbol.set(symbol, {
        type: 'workspace',
        symbol,
        label: symbol,
        stage: row.stage || 'idea',
        setupPattern: row.setup_pattern || null,
        projectIds: [row.id].filter(Boolean),
        createdAt: row.created_at || '',
      })
      continue
    }
    if (row.id && !existing.projectIds.includes(row.id)) existing.projectIds.push(row.id)
    if (stagePriority(row.stage) < stagePriority(existing.stage)) existing.stage = row.stage
    if (!existing.setupPattern && row.setup_pattern) existing.setupPattern = row.setup_pattern
    if (String(row.created_at || '') > String(existing.createdAt || '')) existing.createdAt = row.created_at
  }
  return [...bySymbol.values()].sort((a, b) => {
    const stageDelta = stagePriority(a.stage) - stagePriority(b.stage)
    if (stageDelta !== 0) return stageDelta
    return String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
  })
}

async function verifySymbolForWorkspace(symbol, signal) {
  const normalized = normalizeSearchSymbol(symbol)
  if (!SYMBOL_PATTERN.test(normalized)) return false
  const { data: { session } } = await supabase.auth.getSession()
  const headers = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  const res = await fetch(`/api/ibkr/quote?${new URLSearchParams({ symbol: normalized })}`, { headers, signal })
  if (!res.ok) return false
  const data = await res.json()
  const candidates = [
    data?.last,
    data?.close,
    data?.bid,
    data?.ask,
    data?.['31'],
    data?.['84'],
    data?.['86'],
  ]
  return candidates.some((value) => quoteFieldNumber(value) > 0)
}

function TabContent({ activeTab, primary, executions, tradeIds, notesCount, tz }) {
  if (!primary) return null

  if (activeTab === 'Plan') {
    return (
      <div className="space-y-3">
        {primary.thesis && (
          <section>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Thesis</div>
            <div className="font-mono text-[11px] text-ink whitespace-pre-wrap leading-relaxed">{primary.thesis}</div>
          </section>
        )}
        {primary.setup_notes && (
          <section>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Setup Notes</div>
            <div className="font-mono text-[11px] text-ink whitespace-pre-wrap leading-relaxed">{primary.setup_notes}</div>
          </section>
        )}
        {!primary.thesis && !primary.setup_notes && (
          <div className="font-mono text-xs text-inkMuted text-center py-4">No plan details yet.</div>
        )}
      </div>
    )
  }

  if (activeTab === 'Executions') {
    return executions.length === 0 ? (
      <div className="font-mono text-xs text-inkMuted text-center py-4">No execution data available.</div>
    ) : (
      <div className="space-y-0">
        {executions.map((exec, index) => (
          <div key={exec.id || index} className="flex gap-2">
            <div className="flex flex-col items-center">
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${exec.side === 'sell' ? 'bg-ember' : 'bg-forest'}`} />
              {index < executions.length - 1 && <div className="w-px flex-1 bg-border" />}
            </div>
            <div className="pb-3 flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                  exec.side === 'sell' ? 'bg-ember/10 text-ember border border-ember/30' : 'bg-forest/10 text-forest border border-forest/30'
                }`}>
                  {String(exec.side || '-').toUpperCase()}
                </span>
                <AccountName accountNumber={exec.account_number} className="font-mono text-[10px] text-inkMuted" />
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-0.5">{formatTime(exec.executed_at, tz)}</div>
              <div className="font-mono text-[11px] text-ink mt-0.5">
                {exec.quantity != null ? Math.round(Number(exec.quantity)) : '-'} @ <PrivateValue>{fmtCurrency(exec.price)}</PrivateValue>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (activeTab === 'Notes') {
    return <AssetNotes key={primary.symbol} symbol={primary.symbol}>
      {tradeIds.length > 0 && <details><summary className="text-xs text-inkMuted cursor-pointer">Trade notes</summary><NotesList tradeIds={tradeIds} /></details>}
    </AssetNotes>
  }

  if (activeTab === 'MGOT') {
    return (
      <div className="font-mono text-xs text-inkMuted text-center py-4">
        Coming soon - post-trade review (Made Good On Thesis) will appear here.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Metric label="Pattern" value={primary.setup_pattern || '-'} />
      <Metric label="Trigger" value={fmtCurrency(primary.trigger_price)} />
      <Metric label="Stop" value={fmtCurrency(primary.pattern_failure_price || primary.stop_price)} />
      <Metric label="Target" value={fmtCurrency(primary.sell_target_price)} />
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-canvas2 px-3 py-2">
      <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted">{label}</div>
      <div className="font-mono text-xs text-ink truncate">{value}</div>
    </div>
  )
}

function SymbolSearchModal({
  open,
  query,
  onQueryChange,
  results,
  selectedIndex,
  onSelectedIndexChange,
  verification,
  onClose,
  onSelect,
  inputRef,
}) {
  if (!open) return null
  const resultCount = results.length

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (resultCount > 0) onSelectedIndexChange((selectedIndex + 1) % resultCount)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (resultCount > 0) onSelectedIndexChange((selectedIndex - 1 + resultCount) % resultCount)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const selected = results[selectedIndex]
      if (selected) onSelect(selected)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/30 px-4 pt-[9vh] backdrop-blur-sm dark:bg-black/55" data-testid="position-workspace-symbol-search-modal">
      <div data-testid="position-workspace-symbol-search-panel" className="w-full max-w-3xl rounded-lg border border-border bg-canvas text-ink shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="font-sans text-xl font-semibold text-ink">Symbol search</div>
          <button type="button" onClick={onClose} className="rounded p-1 text-inkMuted hover:bg-canvas2 hover:text-ink" aria-label="Close symbol search">
            <X size={22} />
          </button>
        </div>
        <div className="px-5 pb-5">
          <div className="flex items-center gap-2 rounded-md border border-border bg-canvas2 px-3 py-2 focus-within:border-ink/40">
            <Search size={18} className="text-inkMuted" />
            <input
              ref={inputRef}
              data-testid="position-workspace-symbol-search-input"
              value={query}
              onChange={(event) => onQueryChange(event.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="Search or open symbol"
              className="min-w-0 flex-1 bg-transparent font-mono text-base uppercase text-ink outline-none placeholder:text-inkMuted"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            {query && (
              <button type="button" onClick={() => onQueryChange('')} className="rounded p-1 text-inkMuted hover:text-ink" aria-label="Clear symbol search">
                <X size={16} />
              </button>
            )}
          </div>

          <div className="mt-3 flex gap-2">
            {['All', 'Open', 'Working', 'Ideas'].map((label, index) => (
              <span
                key={label}
                className={`rounded-full px-3 py-1 font-sans text-sm ${index === 0 ? 'bg-ink text-canvas' : 'bg-canvas2 text-inkMuted'}`}
              >
                {label}
              </span>
            ))}
          </div>

          <div className="mt-3 max-h-[56vh] overflow-y-auto border-t border-border">
            {results.map((result, index) => {
              const selected = index === selectedIndex
              const ephemeral = isEphemeralSearchResult(result)
              return (
                <button
                  key={`${result.type}-${result.symbol}`}
                  type="button"
                  data-testid={`position-workspace-symbol-search-option-${result.symbol}`}
                  aria-selected={String(selected)}
                  onMouseEnter={() => onSelectedIndexChange(index)}
                  onClick={() => onSelect(result)}
                  className={`grid w-full grid-cols-[96px_1fr_auto] items-center gap-4 border-b border-border px-3 py-3 text-left transition-colors ${
                    selected ? 'bg-canvas2 text-ink' : 'text-inkMuted hover:bg-canvas2/70 hover:text-ink'
                  }`}
                >
                  <div className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400">{result.symbol}</div>
                  <div className="min-w-0">
                    <div className="truncate font-sans text-sm text-ink">
                      {ephemeral ? `Open ${result.symbol}` : result.label}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">
                      {ephemeral ? ephemeralSearchSubtitle(verification) : `${result.stage || 'workspace'} workspace`}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">
                    {ephemeral ? 'new' : 'open'}
                  </div>
                </button>
              )
            })}
            {verification.status === 'invalid' && (
              <div data-testid="position-workspace-symbol-search-unverified" className="border-b border-border px-3 py-3 font-mono text-xs text-ember">
                Invalid symbol format.
              </div>
            )}
            {results.length === 0 && verification.status === 'checking' && (
              <div className="border-b border-border px-3 py-3 font-mono text-xs text-inkMuted">Checking quote...</div>
            )}
            {results.length === 0 && verification.status === 'unverified' && (
              <div data-testid="position-workspace-symbol-search-unverified" className="border-b border-border px-3 py-3 font-mono text-xs text-inkMuted">
                No verified symbol found. Check the ticker before opening a workspace.
              </div>
            )}
            {results.length === 0 && verification.status === 'idle' && (
              <div className="px-3 py-6 text-center font-mono text-xs text-inkMuted">No workspace positions yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PositionWorkspaceChartLab() {
  const { user, session } = useAuth()
  const commands = useOptionalCommands()
  const registerCommandHandler = commands?.registerCommandHandler
  const { settings } = useSettings()
  const tz = getUserTimezone(settings)
  const location = useLocation()
  const navigate = useNavigate()
  const routeParams = useParams()
  const [searchParams] = useSearchParams()
  const biometricGate = useBiometricGate()
  const { getNickname } = useAccountNicknames()
  const { notifications, upsertNotification } = useNotifications()
  const isMobileLayout = useDemoMobileLayout()

  const routeSymbol = normalizeSearchSymbol(routeParams.symbol)
  const routeProjectId = String(routeParams.projectId || '').trim()
  const handoffSymbol = normalizeSearchSymbol(
    routeSymbol || searchParams.get('symbol') || location.state?.workspaceSymbol,
  )
  const routeProjectIds = useMemo(() => {
    const ids = searchParams.get('ids')
    const projectId = searchParams.get('projectId')
    if (ids) return ids.split(',').map((id) => id.trim()).filter(Boolean)
    if (routeProjectId) return [routeProjectId]
    return projectId ? [projectId] : []
  }, [routeProjectId, searchParams])
  const handoffProjectIds = useMemo(
    () => (routeProjectIds.length > 0
      ? routeProjectIds
      : (Array.isArray(location.state?.workspaceProjectIds) ? location.state.workspaceProjectIds : [])),
    [location.state?.workspaceProjectIds, routeProjectIds],
  )
  const handoffProjectIdsKey = handoffProjectIds.join('|')
  const hasSelectionHandoff = Boolean(
    routeSymbol
      || searchParams.has('symbol')
      || searchParams.has('projectId')
      || searchParams.has('ids')
      || location.state?.workspaceSymbol
      || Array.isArray(location.state?.workspaceProjectIds),
  )

  const [storedSelection] = useState(() => readPositionWorkspaceSelection())
  const [symbol, setSymbol] = useState(() => handoffSymbol || storedSelection?.symbol || '')
  const [requestedIds, setRequestedIds] = useState(() => (
    hasSelectionHandoff ? handoffProjectIds : (storedSelection?.projectIds || [])
  ))
  const [timeframe, setTimeframe] = useState(() => readInitialTimeframe(
    searchParams,
    location.state?.workspaceTimeframe || storedSelection?.timeframe,
  ))
  const [chartMarketSession, setChartMarketSession] = useState('regular')
  const [chartType, setChartType] = useState(readStoredChartType)
  const [projects, setProjects] = useState([])
  const [retiredSetups, setRetiredSetups] = useState([])
  const [setupHistoryError, setSetupHistoryError] = useState(null)
  const [trades, setTrades] = useState([])
  const [executions, setExecutions] = useState([])
  const [loading, setLoading] = useState(true)
  const [notesCount, setNotesCount] = useState(0)
  const [currentPrice, setCurrentPrice] = useState(null)
  const [activeAccounting, setActiveAccounting] = useState(null)
  const [activeStopPrice, setActiveStopPrice] = useState(null)
  const [brokerRefreshVersion, setBrokerRefreshVersion] = useState(0)
  const [positionSwitcherRefreshVersion, setPositionSwitcherRefreshVersion] = useState(0)
  const [cancelingSetupIds, setCancelingSetupIds] = useState(() => new Set())
  const [confirmedRestoredIdea, setConfirmedRestoredIdea] = useState(null)
  const clearConfirmedRestoredIdea = useCallback(() => setConfirmedRestoredIdea(null), [])
  const {
    rowRef: desktopColumnsRef,
    leftWidth,
    rightWidth,
    getResizeHandleProps,
    resizeHandleMarkerClassName,
    layoutPreferences,
  } = useResizableWorkspaceColumns('positionWorkspaceChartLab', { userId: user?.id, expandedRight: true })
  const assetInfoHeight = useServerLayoutNumber({ userId: user?.id, scope: 'positionWorkspaceChartLab', preferenceKey: 'asset-info-height', defaultValue: 42, min: 20, max: 70 })
  const [ledgerTarget, setLedgerTarget] = useState(null)
  const ledgerHeight = useServerLayoutNumber({ userId: user?.id, scope: 'positionWorkspaceChartLab', preferenceKey: 'position-ledger-height', defaultValue: 35, min: 20, max: 70 })
  const workspaceLayoutPreferences = [...layoutPreferences, assetInfoHeight, ledgerHeight]
  const layoutSaveFailed = workspaceLayoutPreferences.some(preference => preference.status === 'error')
  const layoutSaving = workspaceLayoutPreferences.some(preference => preference.status === 'saving')
  const [brokerPositionRows, setBrokerPositionRows] = useState([])
  const [brokerPositionsLoaded, setBrokerPositionsLoaded] = useState(false)
  const [scopedOrdersReadyKey, setScopedOrdersReadyKey] = useState('')

  useEffect(() => {
    if (!handoffSymbol) return
    setSymbol((current) => current === handoffSymbol ? current : handoffSymbol)
  }, [handoffSymbol])
  useEffect(() => {
    if (!hasSelectionHandoff) return
    setRequestedIds((current) => (
      current.join('|') === handoffProjectIdsKey ? current : [...handoffProjectIds]
    ))
  }, [handoffProjectIds, handoffProjectIdsKey, hasSelectionHandoff])
  const [accountsForPortfolio, setAccountsForPortfolio] = useState([])
  const [positionOverlayEnabled, setPositionOverlayEnabled] = useState(true)
  const [selectedSetupId, setSelectedSetupId] = useState(null)
  const setupEditorRef = useRef(null)
  const [setupCloseReview, setSetupCloseReview] = useState(null)
  const [ownedManualSetupDraft, setManualSetupDraft] = useState(null)
  const [ownedPreviewSetupLines, setOwnedPreviewSetupLines] = useState(null)
  const rightTabPreference = useWorkspaceRightTab(user?.id)
  const { value: rightTab, select: setRightTab } = rightTabPreference
  const [placeStopOpen, setPlaceStopOpen] = useState(false)
  const [placeStopPrice, setPlaceStopPrice] = useState('')
  const [stopPriceOverride, setStopPriceOverride] = useState(null)
  const [timelineCollapsed, setTimelineCollapsed] = useState(readStoredTimelineCollapsed)
  const [demoShellHydrated, setDemoShellHydrated] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [assetListSymbolSearchRequested, setAssetListSymbolSearchRequested] = useState(false)
  const handleAssetListSymbolSearchRequestHandled = useCallback(() => {
    setAssetListSymbolSearchRequested(false)
  }, [])
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false)
  const [mobileRightOpen, setMobileRightOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchItems, setSearchItems] = useState([])
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(0)
  const [symbolVerification, setSymbolVerification] = useState({ status: 'idle', symbol: '' })
  const searchInputRef = useRef(null)
  const promotionPromiseRef = useRef(null)
  const userDemoShellActionRef = useRef(false)
  const didOpenSetupFromImportRef = useRef(false)
  const fillMonitorSeenRef = useRef(new Set())
  const fillMonitorInFlightRef = useRef(false)
  const brokerNotificationRefreshSeenRef = useRef(new Set())
  const focusSearchInput = useCallback(() => {
    const input = searchInputRef.current
    if (!input) return
    input.focus({ preventScroll: true })
    input.select()
  }, [])
  const handleTimeframeChange = useCallback((nextTimeframe) => {
    if (!IBKR_RUNTIME_TIMEFRAMES.has(nextTimeframe)) return
    userDemoShellActionRef.current = true
    if (!EXTENDED_HOURS_TIMEFRAMES.has(nextTimeframe)) setChartMarketSession('regular')
    setTimeframe(nextTimeframe)
  }, [])
  const handleMarketSessionChange = useCallback((nextSession) => {
    const normalizedSession = nextSession === 'all' ? 'all' : 'regular'
    if (normalizedSession === 'all' && !EXTENDED_HOURS_TIMEFRAMES.has(timeframe)) return
    userDemoShellActionRef.current = true
    setChartMarketSession(normalizedSession)
  }, [timeframe])
  const handleChartTypeChange = useCallback((nextChartType) => {
    userDemoShellActionRef.current = true
    setChartType(nextChartType)
  }, [])
  const handleTimelineCollapsedChange = useCallback((nextCollapsed) => {
    userDemoShellActionRef.current = true
    setTimelineCollapsed(nextCollapsed)
  }, [])

  const primary = projects[0] || null
  const chartHistory = useChartExecutionHistory(user?.id, symbol || primary?.symbol, executions)
  // A manual draft belongs to the workspace where it was opened. Route
  // handoffs must never relabel its prices as a different symbol/setup.
  const manualSetupDraft = ownedManualSetupDraft
    && ownedManualSetupDraft.ownerSymbol === primary?.symbol
    && ownedManualSetupDraft.ownerProjectId === (primary?.id || null)
    ? ownedManualSetupDraft : null
  const previewSetupLines = ownedPreviewSetupLines
    && ownedPreviewSetupLines.ownerSymbol === primary?.symbol
    && ownedPreviewSetupLines.ownerProjectId === (primary?.id || null)
    ? ownedPreviewSetupLines.lines : null
  const setPreviewSetupLines = useCallback((lines) => {
    setOwnedPreviewSetupLines(lines ? {
      ownerSymbol: primary?.symbol,
      ownerProjectId: primary?.id || null,
      lines,
    } : null)
  }, [primary?.symbol, primary?.id])
  const selectedQuote = useIbkrPositionQuoteSnapshot(primary?.symbol || symbol)
  const selectedSymbol = String(primary?.symbol || symbol || '').toUpperCase()
  const selectedIsOption = isOCCSymbol(selectedSymbol)
  const assetInfo = useAssetInfo(selectedSymbol, user?.id)
  const selectedEquityDisplay = useEquityPriceDisplays(selectedIsOption ? [] : [{ symbol: selectedSymbol }])[selectedSymbol]
  const chartSessionPrice = selectedIsOption ? selectedQuote.price : selectedEquityDisplay?.headlinePrice ?? null
  const chartSession = selectedIsOption ? selectedQuote.session : selectedEquityDisplay?.session ?? null
  const hasRouteHandoff = Boolean(location.pathname && location.pathname !== POSITION_WORKSPACE_FALLBACK_PATH)
    || searchParams.has('symbol')
    || searchParams.has('projectId')
    || searchParams.has('ids')
    || searchParams.has('timeframe')

  useEffect(() => {
    if (!symbol) return
    rememberPositionWorkspaceSelection({ symbol, projectIds: requestedIds, timeframe })
  }, [requestedIds, symbol, timeframe])

  useEffect(() => {
    if (!hasRouteHandoff) return
    // Consume an incoming symbol only after its selection state has caught up.
    // The prior asset's loaded project must not overwrite a notification link.
    if (handoffSymbol && symbol !== handoffSymbol) return
    if (hasSelectionHandoff && requestedIds.join('|') !== handoffProjectIdsKey) return
    if (requestedIds.length > 0 && !primary?.symbol) return
    const selectedSymbol = normalizeSearchSymbol(handoffSymbol || primary?.symbol || symbol)
    if (selectedSymbol) {
      rememberPositionWorkspaceSelection({
        symbol: selectedSymbol,
        projectIds: requestedIds,
        timeframe,
      })
    }
    navigate(POSITION_WORKSPACE_FALLBACK_PATH, {
      replace: true,
      state: selectedSymbol ? {
        workspaceSymbol: selectedSymbol,
        workspaceProjectIds: requestedIds,
        workspaceTimeframe: timeframe,
      } : null,
    })
  }, [
    handoffSymbol,
    handoffProjectIdsKey,
    hasSelectionHandoff,
    hasRouteHandoff,
    navigate,
    primary?.symbol,
    requestedIds,
    symbol,
    timeframe,
  ])

  const stage = String(primary?.stage || 'idea').toLowerCase()
  const isIdea = stage === 'idea'
  const isWorking = stage === 'working'
  const isActive = stage === 'active'
  const isClosed = stage === 'closed' || stage === 'reviewed'
  const setupProjects = useMemo(
    () => projects.filter(project => !isRetiredSetup(project) && hasSetupDetails(project)),
    [projects],
  )
  const handleCancellationPendingChange = useCallback((projectId, pending) => {
    const normalizedId = String(projectId || '')
    if (!normalizedId) return
    setCancelingSetupIds((current) => {
      const next = new Set(current)
      if (pending) next.add(normalizedId)
      else next.delete(normalizedId)
      return next.size === current.size && [...next].every(id => current.has(id)) ? current : next
    })
  }, [])
  useEffect(() => {
    const visibleSetupIds = new Set(setupProjects.map(project => String(project.id)))
    setCancelingSetupIds((current) => {
      const next = new Set([...current].filter(id => visibleSetupIds.has(id)))
      return next.size === current.size ? current : next
    })
  }, [setupProjects])
  const hasSetup = setupProjects.length > 0
  const setupEntryStatuses = useSetupEntryStatus({
    projects: setupProjects, userId: user?.id, accessToken: session?.access_token, notifications,
  })
  const selectedSetupProject = useMemo(
    () => setupProjects.find(project => String(project.id) === String(selectedSetupId)) || null,
    [selectedSetupId, setupProjects],
  )
  const activeCloseReview = setupCloseReview?.projectId === selectedSetupProject?.id ? setupCloseReview : null
  const closeSelectedSetup = () => {
    setSetupCloseReview(null)
    setSelectedSetupId(null)
    setPreviewSetupLines(null)
  }
  const selectSetupCard = (project) => {
    if (setupEditorRef.current?.isSaving?.()) return
    const open = () => {
      setSetupCloseReview(null)
      setSelectedSetupId(project.id || 'historical-setup')
      setManualSetupDraft(null)
      setPreviewSetupLines(null)
    }
    if (selectedSetupProject?.id === project.id) {
      if (activeCloseReview) setSetupCloseReview(null)
      else if (setupEditorRef.current) setupEditorRef.current.requestClose()
      else closeSelectedSetup()
    } else if (selectedSetupProject && setupEditorRef.current) {
      setupEditorRef.current.requestClose(open)
    } else open()
  }
  const tradeIds = useMemo(() => trades.map((trade) => trade.id).filter(Boolean), [trades])
  const trackedOrderIdsForVisiblePositions = useMemo(
    () => projects.flatMap((project) => (Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : [])),
    [projects],
  )
  const workspaceAccountNumbers = useMemo(
    () => {
      const symbolUpper = primary?.symbol?.toUpperCase()
      const sizingAccounts = projects.flatMap((project) => (project.account_sizing || []).map((row) => row.account_number))
      const tradeAccounts = trades
        .filter((trade) =>
          (trade.status === 'open' || trade.status === 'partial') &&
          (!symbolUpper || trade.symbol?.toUpperCase() === symbolUpper)
        )
        .map((trade) => trade.account_number)
      const brokerAccounts = brokerPositionRows
        .filter((row) =>
          (!row.status || String(row.status).toLowerCase() === 'open') &&
          (!symbolUpper || row.symbol?.toUpperCase() === symbolUpper)
        )
        .map((row) => row.account_number)
      return normalizeAccountNumbers([...sizingAccounts, ...tradeAccounts, ...brokerAccounts])
    },
    [projects, trades, brokerPositionRows, primary?.symbol],
  )
  const workspaceAccountKey = workspaceAccountNumbers.join(',')

  const {
    orders: wsOrders,
    rawOrders: wsRawOrders,
    ready: wsOrdersReady,
    fetchOrders: wsFetchOrders,
    refreshForSymbol: wsRefreshForSymbol,
    getStopOrdersForSymbol: wsGetStopOrders,
    conditionalStopKeys: wsConditionalStopKeys,
  } = useWorkingOrders({ trackedOrderIds: trackedOrderIdsForVisiblePositions })
  const scopedOrdersKey = primary?.symbol && workspaceAccountKey
    ? `${primary.symbol.toUpperCase()}::${workspaceAccountKey}`
    : ''
  const scopedOrdersReady = (!isActive && !isWorking) || !scopedOrdersKey
    ? wsOrdersReady
    : wsOrdersReady && scopedOrdersReadyKey === scopedOrdersKey
  const workingOrdersApi = useMemo(() => ({
    orders: wsOrders,
    rawOrders: wsRawOrders,
    ready: scopedOrdersReady,
    fetchOrders: wsFetchOrders,
    refreshForSymbol: wsRefreshForSymbol,
    getStopOrdersForSymbol: wsGetStopOrders,
    conditionalStopKeys: wsConditionalStopKeys,
  }), [
    wsOrders,
    wsRawOrders,
    scopedOrdersReady,
    wsFetchOrders,
    wsRefreshForSymbol,
    wsGetStopOrders,
    wsConditionalStopKeys,
  ])
  const projectSelectionKey = JSON.stringify([user?.id, symbol, requestedIds])
  const projectSelectionRef = useRef(projectSelectionKey)
  projectSelectionRef.current = projectSelectionKey
  const loadProjects = useCallback(async () => {
    const selectionAtStart = JSON.stringify([user?.id, symbol, requestedIds])
    if (!user?.id) return
    setLoading(true)

    let loadedProjects = []
    if (requestedIds.length > 0) {
      const { data, error } = await supabase
        .from('position_projects')
        .select('*')
        .eq('user_id', user.id)
        .in('id', requestedIds)
      if (error) throw new Error(error.message)
      const requestedById = new Map((data || []).map((project) => [String(project.id), project]))
      // Preserve explicit card identity and request order. Besides making the
      // selected setup deterministic, the local intersection fails closed if
      // a backend/mock ever returns rows outside the requested id scope.
      loadedProjects = requestedIds
        .map((id) => requestedById.get(String(id)))
        .filter(Boolean)
    } else if (symbol) {
      const { data, error } = await supabase
        .from('position_projects')
        .select('*')
        .eq('user_id', user.id)
        .eq('symbol', symbol)
        .in('stage', ['active', 'working', 'idea', 'planned', 'closed', 'reviewed'])
        .eq('is_draft', false)
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      const symbolProjects = (data || []).filter(isWorkspaceProject)
      const currentProjects = symbolProjects.filter(project => (
        ['active', 'working', 'idea', 'planned'].includes(String(project.stage || '').toLowerCase())
      ))
      const symbolPrimary = pickPrimaryProject(symbolProjects)
      loadedProjects = currentProjects.length > 0
        ? currentProjects
        : symbolPrimary
          ? symbolProjects.filter((project) => project.stage === symbolPrimary.stage)
        : []
    } else if (location.pathname === POSITION_WORKSPACE_FALLBACK_PATH) {
      const { data, error } = await supabase
        .from('position_projects')
        .select('*')
        .eq('user_id', user.id)
        .eq('stage', 'active')
        .eq('is_draft', false)
        .order('updated_at', { ascending: false })
        .limit(1)
      if (error) throw new Error(error.message)
      loadedProjects = data || []
    } else {
      // Compatibility/demo mounts without explicit route identity keep their
      // historical fixture behavior. The real canonical workspace intentionally
      // prefers a remembered ticker or one active position.
      const { data, error } = await supabase
        .from('position_projects')
        .select('*')
        .eq('user_id', user.id)
        .in('stage', ['active', 'working', 'idea', 'planned'])
        .eq('is_draft', false)
        .order('created_at', { ascending: false })
        .limit(1)
      if (error) throw new Error(error.message)
      loadedProjects = data || []
    }

    const loadedSymbol = loadedProjects[0]?.symbol || symbol
    loadedProjects = loadedProjects.filter(isWorkspaceProject)
    let mergedProjects = loadedProjects
    const symbols = [...new Set([...loadedProjects.map((p) => p.symbol), loadedSymbol].filter(Boolean))]
    const stages = [...new Set(loadedProjects.map((p) => p.stage).filter(Boolean))]
    const hasCurrentLifecycle = loadedProjects.some(project => (
      ['active', 'working', 'idea', 'planned'].includes(String(project.stage || '').toLowerCase())
    ))
    if (symbols.length > 0) {
      const { data: siblings, error } = await supabase
        .from('position_projects')
        .select('*')
        .eq('user_id', user.id)
        .in('symbol', symbols)
        .in('stage', hasCurrentLifecycle || !stages.length ? ['active', 'working', 'idea', 'planned'] : stages)
      if (error) throw new Error(error.message)
      const byId = new Map()
      for (const row of siblings || []) {
        if (isWorkspaceProject(row)) byId.set(row.id, row)
      }
      for (const row of loadedProjects) byId.set(row.id, row)
      mergedProjects = [...byId.values()]
    }

    mergedProjects = mergedProjects.map(projectSetupLifecycle)
    const primaryProject = pickPrimaryProject(mergedProjects)
    const effectiveSymbol = primaryProject?.symbol || loadedSymbol
    const effectiveStages = [...new Set(mergedProjects.map((p) => p.stage))]
    const activeLike = effectiveStages.some((s) => s === 'active' || s === 'working')

    let nextRetiredSetups = []
    let nextHistoryError = null
    if (effectiveSymbol) {
      try {
        const { data, error } = await supabase.from('position_projects')
          .select('*')
          .eq('user_id', user.id)
          .eq('symbol', effectiveSymbol)
          .in('setup_status', ['canceled', 'replaced', 'cleared'])
          .eq('is_draft', false)
          .order('closed_at', { ascending: false })
        if (error) throw error
        const { data: lineage, error: lineageError } = await supabase.from('position_projects')
          .select('id, user_id, symbol, restored_from_setup_id')
          .eq('user_id', user.id)
          .eq('symbol', effectiveSymbol)
        if (lineageError) throw lineageError
        nextRetiredSetups = visibleSetupHistory(
          (data || []).filter(project => isRetiredSetup(project) && project.symbol === effectiveSymbol),
          lineage || [],
        )
      } catch {
        // History availability must not prevent current positions from loading.
        nextHistoryError = 'Setup history is temporarily unavailable.'
      }
    }

    let nextTrades = []
    if (activeLike && effectiveSymbol) {
      const { data, error } = await supabase
        .from('trades')
        .select('*')
        .eq('user_id', user.id)
        .eq('symbol', effectiveSymbol)
        .in('status', ['open', 'partial'])
        .order('entry_date', { ascending: true })
      if (error) throw new Error(error.message)
      nextTrades = data || []
    } else {
      const linkedTradeIds = [...new Set(mergedProjects.map((p) => p.trade_id).filter(Boolean))]
      if (linkedTradeIds.length > 0) {
        const { data, error } = await supabase
          .from('trades')
          .select('*')
          .in('id', linkedTradeIds)
          .neq('status', 'duplicate')
        if (error) throw new Error(error.message)
        nextTrades = data || []
      }
    }

    let nextExecutions = []
    const nextTradeIds = [...new Set(nextTrades.map((trade) => trade.id).filter(Boolean))]
    if (nextTradeIds.length > 0) {
      const { data, error } = await supabase
        .from('executions')
        .select('*')
        .in('trade_id', nextTradeIds)
        .order('executed_at', { ascending: true })
      if (error) throw new Error(error.message)
      nextExecutions = data || []
    }

    // A late response for the previous asset must not replace the new chart.
    if (projectSelectionRef.current !== selectionAtStart) return
    setProjects(primaryProject
      ? [primaryProject, ...mergedProjects.filter((p) => p.id !== primaryProject.id)]
      : (effectiveSymbol ? [buildEphemeralProject(effectiveSymbol, user.id)] : [])
    )
    setRetiredSetups(nextRetiredSetups)
    setSetupHistoryError(nextHistoryError)
    setTrades(nextTrades)
    setExecutions(nextExecutions)
    if (primaryProject?.symbol && primaryProject.symbol !== symbol) setSymbol(primaryProject.symbol)
    setLoading(false)
  }, [location.pathname, requestedIds, symbol, user?.id])

  useEffect(() => {
    let cancelled = false
    loadProjects().catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [loadProjects])

  useEffect(() => {
    if (!user?.id) return
    const targetSymbol = String(primary?.symbol || symbol || '').trim().toUpperCase()
    if (!targetSymbol) return
    const relevantNotifications = (Array.isArray(notifications) ? notifications : [])
      .filter((notification) => notification?.serverBacked === true)
      .filter((notification) => notification?.id && !brokerNotificationRefreshSeenRef.current.has(notification.id))
      .filter((notification) => String(notification?.symbol || '').trim().toUpperCase() === targetSymbol)
      .filter((notification) => notification?.brokerEventId || notification?.type)
    if (relevantNotifications.length === 0) return

    for (const notification of relevantNotifications) {
      brokerNotificationRefreshSeenRef.current.add(notification.id)
    }
    setBrokerRefreshVersion((version) => version + 1)
    setPositionSwitcherRefreshVersion((version) => version + 1)
    loadProjects().catch(() => null)
    if (typeof wsRefreshForSymbol === 'function') {
      wsRefreshForSymbol(targetSymbol, workspaceAccountNumbers).catch(() => null)
    }
  }, [
    notifications,
    user?.id,
    primary?.symbol,
    symbol,
    loadProjects,
    wsRefreshForSymbol,
    workspaceAccountKey,
  ])

  useEffect(() => {
    if (!user?.id) {
      setDemoShellHydrated(true)
      return undefined
    }
    let cancelled = false
    setDemoShellHydrated(false)
    const urlHasTimeframe = IBKR_RUNTIME_TIMEFRAMES.has(searchParams.get('timeframe'))

    async function hydrateDemoShellPreferences() {
      try {
        const payload = await readServerChartPreference({
          scope: DEMO_CHART_PREFERENCE_SCOPE,
          preferenceKey: CHART_PREFERENCE_KEYS.demoShell,
          userId: user.id,
        })
        if (cancelled || payload?.version !== 1) return

        const nextChartType = VALID_CHART_TYPES.has(payload.chartType) ? payload.chartType : chartType
        const nextTimeframe = !urlHasTimeframe && IBKR_RUNTIME_TIMEFRAMES.has(payload.timeframe)
          ? payload.timeframe
          : timeframe
        const nextTimelineCollapsed = typeof payload.timelineCollapsed === 'boolean'
          ? payload.timelineCollapsed
          : timelineCollapsed
        const nextMarketSession = payload.marketSession === 'all'
          && EXTENDED_HOURS_TIMEFRAMES.has(nextTimeframe)
          ? 'all'
          : 'regular'

        setChartType(nextChartType)
        setTimeframe(nextTimeframe)
        setTimelineCollapsed(nextTimelineCollapsed)
        setChartMarketSession(nextMarketSession)
      } finally {
        if (!cancelled) setDemoShellHydrated(true)
      }
    }

    hydrateDemoShellPreferences()

    return () => {
      cancelled = true
    }
    // Hydration intentionally runs once per authenticated user. `searchParams`
    // is sampled at mount so explicit URL timeframe keeps precedence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return undefined
    if (!demoShellHydrated) return undefined
    if (!userDemoShellActionRef.current) return undefined

    const payload = demoShellPreferencePayload({
      chartType,
      timeframe,
      timelineCollapsed,
      marketSession: EXTENDED_HOURS_TIMEFRAMES.has(timeframe) ? chartMarketSession : 'regular',
    })
    void writeServerChartPreference({
      scope: DEMO_CHART_PREFERENCE_SCOPE,
      preferenceKey: CHART_PREFERENCE_KEYS.demoShell,
      preferences: payload,
      userId: user.id,
    })
    return undefined
  }, [user?.id, chartType, timeframe, timelineCollapsed, chartMarketSession, demoShellHydrated])

  useEffect(() => {
    if (!registerCommandHandler) return
    return registerCommandHandler('position.asset.search', () => {
      if (primary) {
        setAssetListSymbolSearchRequested(true)
        if (isMobileLayout) setMobileLeftOpen(true)
      } else {
        setSearchQuery('')
        setSearchSelectedIndex(0)
        setSymbolVerification({ status: 'idle', symbol: '' })
        setSearchOpen(true)
      }
      return { ok: true }
    })
  }, [isMobileLayout, primary, registerCommandHandler])

  useEffect(() => {
    if (!searchOpen) return
    const id = requestAnimationFrame(focusSearchInput)
    return () => cancelAnimationFrame(id)
  }, [focusSearchInput, searchOpen])

  useEffect(() => {
    if (!searchOpen || !user?.id) return
    let cancelled = false
    supabase
      .from('position_projects')
      .select('id, symbol, stage, created_at, setup_pattern')
      .eq('user_id', user.id)
      .in('stage', ['active', 'working', 'idea', 'planned'])
      .eq('is_draft', false)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!cancelled) setSearchItems(groupSearchProjects(data || []))
      })
    return () => { cancelled = true }
  }, [searchOpen, user?.id])

  const normalizedSearchQuery = normalizeSearchSymbol(searchQuery)
  const workspaceSearchResults = useMemo(() => {
    if (!normalizedSearchQuery) return searchItems
    return searchItems.filter((item) => item.symbol.includes(normalizedSearchQuery))
  }, [normalizedSearchQuery, searchItems])
  const exactWorkspaceMatch = useMemo(
    () => searchItems.some((item) => item.symbol === normalizedSearchQuery),
    [normalizedSearchQuery, searchItems],
  )

  useEffect(() => {
    setSearchSelectedIndex(0)
  }, [normalizedSearchQuery])

  useEffect(() => {
    if (!searchOpen || !normalizedSearchQuery || exactWorkspaceMatch) {
      setSymbolVerification({ status: 'idle', symbol: normalizedSearchQuery })
      return
    }
    if (!SYMBOL_PATTERN.test(normalizedSearchQuery)) {
      setSymbolVerification({ status: 'invalid', symbol: normalizedSearchQuery })
      return
    }
    const controller = new AbortController()
    setSymbolVerification({ status: 'checking', symbol: normalizedSearchQuery })
    const timeout = setTimeout(() => {
      verifySymbolForWorkspace(normalizedSearchQuery, controller.signal)
        .then((verified) => {
          if (!controller.signal.aborted) {
            setSymbolVerification({ status: verified ? 'verified' : 'unverified', symbol: normalizedSearchQuery })
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSymbolVerification({ status: 'unverified', symbol: normalizedSearchQuery })
          }
        })
    }, 250)
    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [searchOpen, normalizedSearchQuery, exactWorkspaceMatch])

  const symbolSearchResults = useMemo(() => {
    const results = [...workspaceSearchResults]
    if (
      normalizedSearchQuery
      && SYMBOL_PATTERN.test(normalizedSearchQuery)
      && !exactWorkspaceMatch
    ) {
      results.push({
        type: symbolVerification.status === 'verified' && symbolVerification.symbol === normalizedSearchQuery
          ? 'verified'
          : 'ephemeral',
        symbol: normalizedSearchQuery,
        label: normalizedSearchQuery,
        stage: 'ephemeral',
        projectIds: [],
      })
    }
    return results
  }, [workspaceSearchResults, symbolVerification, normalizedSearchQuery, exactWorkspaceMatch])

  useEffect(() => {
    setSearchSelectedIndex((index) => Math.min(index, Math.max(0, symbolSearchResults.length - 1)))
  }, [symbolSearchResults.length])

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    supabase
      .from('schwab_accounts')
      .select('account_number, equity')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (!cancelled) setAccountsForPortfolio(data || [])
      })
    return () => { cancelled = true }
  }, [user?.id])

  useEffect(() => {
    if (!primary?.symbol) return
    if (!isActive && !isWorking && !(isIdea && hasSetup)) return
    if ((isActive || isWorking) && !scopedOrdersKey) return
    setScopedOrdersReadyKey('')
    const controller = new AbortController()
    const refresh = wsRefreshForSymbol(primary.symbol, workspaceAccountNumbers, { signal: controller.signal })
    if (refresh && typeof refresh.then === 'function') {
      refresh.then((result) => {
        if (controller.signal.aborted || !result) return
        if (scopedOrdersKey) setScopedOrdersReadyKey(scopedOrdersKey)
        setBrokerRefreshVersion((v) => v + 1)
      })
    }
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isWorking, isIdea, hasSetup, primary?.symbol, scopedOrdersKey, workspaceAccountKey, wsRefreshForSymbol])

  useEffect(() => {
    if (!user?.id || !isActive || !primary?.symbol) {
      setBrokerPositionRows([])
      setBrokerPositionsLoaded(false)
      return
    }
    let cancelled = false
    setBrokerPositionsLoaded(false)
    supabase
      .from('schwab_positions')
      .select('account_number, symbol, quantity, status')
      .eq('user_id', user.id)
      .eq('symbol', primary.symbol)
      .then(({ data }) => {
        if (cancelled) return
        setBrokerPositionRows((data || []).filter((p) => !p.status || String(p.status).toLowerCase() === 'open'))
        setBrokerPositionsLoaded(true)
      })
    return () => { cancelled = true }
  }, [user?.id, isActive, primary?.symbol, brokerRefreshVersion])

  const liveWorkingStopOrders = useMemo(() => {
    if (!scopedOrdersReady || !isActive || !primary?.symbol) return []
    return wsGetStopOrders(primary.symbol, { includeAwaitingParent: false })
  }, [scopedOrdersReady, isActive, primary?.symbol, wsGetStopOrders])

  const liveStopLines = useMemo(() => buildStopLines(liveWorkingStopOrders), [liveWorkingStopOrders])

  const liveStopPrice = liveStopLines[0]?.price ?? null
  const openTrades = useMemo(
    () => trades.filter((trade) =>
      (trade.status === 'open' || trade.status === 'partial') &&
      trade.symbol?.toUpperCase() === primary?.symbol?.toUpperCase()
    ),
    [trades, primary?.symbol],
  )

  const protection = useMemo(() => resolveWorkspaceProtection({
    isActive,
    symbol: primary?.symbol,
    ordersReady: scopedOrdersReady,
    stopOrders: liveWorkingStopOrders,
    brokerPositions: brokerPositionRows,
    brokerPositionsReady: brokerPositionsLoaded,
    openTrades,
  }), [scopedOrdersReady, isActive, primary?.symbol, liveWorkingStopOrders, brokerPositionsLoaded, brokerPositionRows, openTrades])

  useEffect(() => {
    if (stopPriceOverride == null || !scopedOrdersReady) return
    if (liveStopPrice != null && Math.abs(liveStopPrice - stopPriceOverride) < 0.01) {
      setStopPriceOverride(null)
    }
  }, [scopedOrdersReady, liveStopPrice, stopPriceOverride])

  const staticData = useMemo(() => {
    if (projects.length === 0) return null
    // IDEA/WORKING setup fields are the operator-reviewed order intent. Keep
    // the header aligned with the setup card instead of older planning fields
    // that may still exist on the same project row.
    const headerSetupProject = selectedSetupProject || primary
    const sizingSource = Array.isArray(previewSetupLines?.accountSizing)
      ? previewSetupLines.accountSizing
      : (headerSetupProject?.account_sizing || [])
    const allSizing = sizingSource
      .map((sizing) => ({ ...sizing, projectId: headerSetupProject.id }))
    return {
      allSizing,
      entryPrice: headerSetupProject?.trigger_price ?? headerSetupProject?.entry_price ?? null,
      stopPrice: headerSetupProject?.pattern_failure_price ?? headerSetupProject?.stop_price ?? null,
      targetPrice: headerSetupProject?.sell_target_price ?? headerSetupProject?.target_price ?? null,
      totalShares: allSizing.reduce((sum, row) => sum + (Number(row.shares) || 0), 0),
      totalMaxLoss: allSizing.reduce((sum, row) => sum + (Number(row.max_loss) || 0), 0),
      totalPnl: trades.length > 0 ? trades.reduce((sum, trade) => sum + (Number(trade.net_pnl) || 0), 0) : null,
      accountCount: allSizing.length,
    }
  }, [projects, primary, selectedSetupProject, previewSetupLines, trades])

  const chartData = useMemo(() => {
    if (!primary?.symbol) return null
    const linkedTrade = trades[0]
    const entryDate = linkedTrade?.entry_date || primary?.created_at?.slice(0, 10)
    const entryPrice = isActive && activeAccounting?.remainingAvgPrice
      ? activeAccounting.remainingAvgPrice
      : linkedTrade?.avg_entry_price ?? primary?.entry_price ?? null
    const brokerVerifiedStopLoss = stopPriceOverride ?? liveStopPrice ?? null
    const dbFallbackStopLoss = linkedTrade?.stop_loss ?? primary?.stop_price ?? primary?.pattern_failure_price ?? null
    const stopLoss = isActive
      ? (scopedOrdersReady ? brokerVerifiedStopLoss : null)
      : (brokerVerifiedStopLoss ?? dbFallbackStopLoss)
    return { entryDate, entryPrice, stopLoss }
  }, [primary, trades, isActive, scopedOrdersReady, liveStopPrice, stopPriceOverride, activeAccounting?.remainingAvgPrice])

  const liveSellLimitPrice = useMemo(() => {
    if (!scopedOrdersReady || !isActive || !primary?.symbol) return null
    const liveSellLimitPrices = collectLiveSellLimitPrices([
      ...(Array.isArray(wsRawOrders) ? wsRawOrders : []),
      ...(Array.isArray(wsOrders) ? wsOrders : []),
    ], primary.symbol, workspaceAccountNumbers)
    return liveSellLimitPrices.length > 0 ? Math.min(...liveSellLimitPrices) : null
  }, [scopedOrdersReady, isActive, primary?.symbol, wsRawOrders, wsOrders, workspaceAccountNumbers])

  const savedSetupLines = useMemo(() => {
    if (previewSetupLines) return previewSetupLines
    if (!primary) return null
    if (isClosed) {
      return selectedSetupProject ? compactSetupLines(selectedSetupProject) : null
    }
    if (isActive) {
      return liveSellLimitPrice ? { sellTargetPrice: liveSellLimitPrice } : null
    }
    if (!hasSetup) return null
    return compactSetupLines(selectedSetupProject || primary)
  }, [hasSetup, previewSetupLines, isClosed, selectedSetupProject, isActive, primary, liveSellLimitPrice])

  const getOverlayProps = useCallback((chartTimeframe, enabled) => {
    const overlay = buildPositionOverlayProps({
      executions: consolidateExecutions(chartHistory.executions, { timeframe: chartTimeframe, timezone: tz }),
      setupLines: savedSetupLines,
      stopLines: isActive ? liveStopLines : null,
      stopLoss: isActive ? (chartData?.stopLoss ?? null) : null,
      activeStopPrice,
      entryPrice: chartData?.entryPrice ?? null,
      assetType: primary?.asset_type || primary?.assetType || 'equity',
      stage,
      timeframe: chartTimeframe,
      timezone: tz,
    })
    return enabled ? overlay : { ...overlay, setupLines: null, positionLines: null }
  }, [chartHistory.executions, savedSetupLines, isActive, liveStopLines, chartData, activeStopPrice, primary, stage, tz])

  const portfolioValue = useMemo(
    () => accountsForPortfolio.reduce((sum, account) => sum + (Number(account.equity) || 0), 0),
    [accountsForPortfolio],
  )

  const handleSymbolChange = useCallback((nextSymbol) => {
    const normalized = normalizeSearchSymbol(nextSymbol)
    if (!normalized || normalized === primary?.symbol) return
    setSearchQuery(normalized)
    setSearchSelectedIndex(0)
    setSymbolVerification({ status: 'idle', symbol: normalized })
    setSearchOpen(true)
  }, [primary?.symbol])

  const openSymbolSearch = useCallback((seedSymbol = primary?.symbol || symbol) => {
    const normalized = normalizeSearchSymbol(seedSymbol)
    flushSync(() => {
      setSearchQuery(normalized)
      setSearchSelectedIndex(0)
      setSymbolVerification({ status: 'idle', symbol: normalized })
      setSearchOpen(true)
    })
    focusSearchInput()
  }, [focusSearchInput, primary?.symbol, symbol])

  const openEphemeralSymbol = useCallback((nextSymbol) => {
    const normalized = normalizeSearchSymbol(nextSymbol)
    if (!SYMBOL_PATTERN.test(normalized) && !isOCCSymbol(normalized)) return
    promotionPromiseRef.current = null
    setSelectedSetupId(null)
    setManualSetupDraft(null)
    setPreviewSetupLines(null)
    setSearchOpen(false)
    setRequestedIds([])
    setSymbol(normalized)
    setProjects([buildEphemeralProject(normalized, user?.id)])
    setTrades([])
    setExecutions([])
    navigate(buildPositionWorkspaceTarget({
      symbol: normalized,
      timeframe,
    }), { replace: true })
  }, [navigate, timeframe, user?.id])

  const openWorkspaceSearchResult = useCallback((result) => {
    if (isEphemeralSearchResult(result)) {
      openEphemeralSymbol(result.symbol)
      return
    }
    const ids = result.projectIds || []
    if (ids.length === 0) return
    promotionPromiseRef.current = null
    setSelectedSetupId(null)
    setManualSetupDraft(null)
    setPreviewSetupLines(null)
    setSearchOpen(false)
    setRequestedIds(ids)
    setSymbol(result.symbol)
    setProjects([buildEphemeralProject(result.symbol, user?.id, {
      stage: result.stage || 'idea',
      __workspaceShell: true,
    })])
    setTrades([])
    setExecutions([])
    navigate(buildPositionWorkspaceTarget({
      symbol: result.symbol,
      projectIds: ids,
      timeframe,
    }), { replace: true })
  }, [navigate, openEphemeralSymbol, timeframe, user?.id])

  const handleStopMoved = useCallback((newPrice) => {
    setStopPriceOverride(newPrice)
    if (primary?.symbol) {
      setTimeout(() => wsRefreshForSymbol(primary.symbol, workspaceAccountNumbers), 2000)
      setTimeout(() => wsRefreshForSymbol(primary.symbol, workspaceAccountNumbers), 7000)
    }
  }, [primary?.symbol, workspaceAccountNumbers, wsRefreshForSymbol])

  const handlePositionRefresh = useCallback(async () => {
    if (primary?.symbol) {
      const refreshed = await wsRefreshForSymbol(primary.symbol, workspaceAccountNumbers)
      if (refreshed) {
        if (scopedOrdersKey) setScopedOrdersReadyKey(scopedOrdersKey)
        setBrokerRefreshVersion((v) => v + 1)
      }
    }
    await loadProjects()
    setPositionSwitcherRefreshVersion((v) => v + 1)
  }, [loadProjects, primary?.symbol, scopedOrdersKey, workspaceAccountNumbers, wsRefreshForSymbol])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const shouldRefreshScopedOrders =
      !!primary?.symbol &&
      !isClosed &&
      (isActive || isWorking || (isIdea && hasSetup)) &&
      (!isActive || !!scopedOrdersKey)

    if (!shouldRefreshScopedOrders) return undefined

    const handleSchwabSyncComplete = () => {
      handlePositionRefresh().catch((err) => {
        console.error('[PositionWorkspaceChartLab] Scoped refresh after Schwab sync failed:', err)
      })
    }

    window.addEventListener(SCHWAB_SYNC_COMPLETE_EVENT, handleSchwabSyncComplete)
    return () => window.removeEventListener(SCHWAB_SYNC_COMPLETE_EVENT, handleSchwabSyncComplete)
  }, [handlePositionRefresh, primary?.symbol, isClosed, isActive, isWorking, isIdea, hasSetup, scopedOrdersKey])

  const emptyIdeaShell = ['idea', 'planned'].includes(stage) && !hasSetupDetails(primary) && !primary?.trade_id && primary?.setup_source !== 'Ideas List'
  const cancelSavedIdea = async (project) => {
    await retireUnfilledSetup(supabase, project, { userId: user?.id })
    setSelectedSetupId(null)
    setManualSetupDraft(null)
    setPreviewSetupLines(null)
    await handlePositionRefresh()
  }
  const restoreHistoricalSetup = async (project, newId) => {
    const created = await restoreSetupAsIdea(supabase, project, { userId: user?.id, newId })
    // Show the restored card immediately; the original history remains intact.
    setProjects(current => [created, ...current.filter(row => row.id && row.id !== created.id)])
    setRetiredSetups(current => current.filter(row => row.id !== project.id))
    setSelectedSetupId(null)
    setManualSetupDraft(null)
    setPreviewSetupLines(null)
    setConfirmedRestoredIdea(created)
    setPositionSwitcherRefreshVersion(v => v + 1)
  }
  const manualSetupCreatesFreshProject = !!manualSetupDraft && (isClosed || emptyIdeaShell) && !!primary?.symbol
  const manualSetupProject = useMemo(() => {
    if (!manualSetupCreatesFreshProject) return primary
    return buildEphemeralProject(primary.symbol, user?.id)
  }, [manualSetupCreatesFreshProject, primary, user?.id])

  const isEphemeral = primary?.id == null && !!primary?.symbol && !primary?.__workspaceShell
  const insertEphemeralProject = useCallback((initialFields = {}) => {
    const canInsertFreshProject = isEphemeral || isClosed || emptyIdeaShell
    if (!canInsertFreshProject || !user?.id || !primary?.symbol) return Promise.resolve(null)
    if (promotionPromiseRef.current) return promotionPromiseRef.current
    const run = (async () => {
      const { data: created, error } = await supabase
        .from('position_projects')
        .insert({
          user_id: user.id,
          symbol: primary.symbol.toUpperCase(),
          stage: 'idea',
          direction: 'LONG',
          is_draft: false,
          ...initialFields,
        })
        .select('*')
        .single()
      if (error) throw new Error(error.message)
      // Saving this draft assigns its first/new lifecycle id; that is not
      // a workspace switch. Keep its pending confirmation mounted.
      setManualSetupDraft(draft => draft
        && draft.ownerSymbol === primary.symbol
        && draft.ownerProjectId === (primary.id || null)
        ? { ...draft, ownerProjectId: created.id } : draft)
      setProjects([created])
      return created
    })()

    promotionPromiseRef.current = run.finally(() => {
      promotionPromiseRef.current = null
    })
    return promotionPromiseRef.current
  }, [isEphemeral, isClosed, emptyIdeaShell, primary?.symbol, primary?.id, user?.id])

  const onEphemeralFinalized = useCallback((createdProject) => {
    if (!createdProject?.id) return
    navigate(buildPositionWorkspaceTarget({
      symbol: createdProject.symbol || primary?.symbol || symbol,
      projectId: createdProject.id,
      timeframe,
    }), { replace: true })
  }, [navigate, primary?.symbol, symbol, timeframe])

  const assetIdeas = useAddAssetIdea(user?.id, ({ project, stage: savedStage }) => {
    setPositionSwitcherRefreshVersion(version => version + 1)
    if (savedStage === 'idea') setConfirmedRestoredIdea(project)
    if (project?.symbol === primary?.symbol && !primary?.id) setProjects([project])
  })

  const { confirmState, showConfirmation, confirmOrder, cancelConfirmation } = useOrderGateway({
    biometricGate,
    onOrdersChanged: handlePositionRefresh,
  })

  const handlePlaceStopConfirm = useCallback(() => {
    const price = parseFloat(placeStopPrice)
    if (!price || price <= 0 || !primary?.symbol) return

    const symbolUpper = primary.symbol.toUpperCase()
    const orderSummaries = protection.accountGaps.map((gap) => ({
      accountName: getNickname(gap.accountNumber) || `****${gap.accountNumber.slice(-4)}`,
      accountNumber: gap.accountNumber,
      quantity: gap.gap,
      side: 'SELL',
      orderType: 'STOP',
      stopPrice: price,
    }))
    const gatewayOrders = protection.accountGaps.map((gap) => ({
      account_number: gap.accountNumber,
      symbol: symbolUpper,
      side: 'SELL',
      quantity: gap.gap,
      orderType: 'STOP',
      stopPrice: price,
      duration: 'GOOD_TILL_CANCEL',
      session: 'NORMAL',
      assetType: 'EQUITY',
    }))

    setPlaceStopOpen(false)
    setPlaceStopPrice('')
    showConfirmation('PLACE', symbolUpper, orderSummaries, gatewayOrders)
  }, [placeStopPrice, primary?.symbol, protection.accountGaps, getNickname, showConfirmation])

  const handleImmediateSetupRepair = useCallback(() => {
    const repairSymbol = String(primary?.symbol || '').trim().toUpperCase()
    if (!repairSymbol) return
    const params = new URLSearchParams()
    params.set('symbol', repairSymbol)
    if (primary?.id) params.set('projectId', primary.id)
    params.set('repairSetup', 'missing')
    navigate({ pathname: '/positions/workspace', search: `?${params.toString()}` }, { replace: true })
  }, [navigate, primary?.id, primary?.symbol])

  useEffect(() => {
    if (
      location.state?.fromImport &&
      primary?.id &&
      !didOpenSetupFromImportRef.current
    ) {
      didOpenSetupFromImportRef.current = true
      setRightTab('setups')
    }
  }, [location.state?.fromImport, primary?.id])

  useEffect(() => {
    if (manualSetupDraft || selectedSetupId) setRightTab('setups')
  }, [manualSetupDraft, selectedSetupId])

  useEffect(() => {
    if (searchParams.get('repairSetup') !== 'missing') return
    setRightTab('setups')
    if (isMobileLayout) setMobileRightOpen(true)
  }, [isMobileLayout, searchParams])

  const rightTabs = useMemo(() => RIGHT_TABS, [])
  const extendedHoursEnabled = EXTENDED_HOURS_TIMEFRAMES.has(timeframe)
  const effectiveChartMarketSession = extendedHoursEnabled ? chartMarketSession : 'regular'

  useEffect(() => {
    if (!rightTabs.includes(rightTab)) setRightTab(rightTabs[0] || 'actions')
  }, [rightTabs, rightTab])

  if ((loading && projects.length === 0) || !demoShellHydrated) {
    return <div className="font-mono text-xs text-inkMuted animate-pulse p-4">Loading position workspace lab...</div>
  }

  if (!primary) {
    return (
      <PositionWorkspaceCommandSurface
        currentSymbol={primary?.symbol || symbol}
        assetIdeas={assetIdeas}
        className="space-y-4"
        data-testid="position-workspace-neutral-state"
        onShowActions={() => {
          setRightTab('actions')
          if (isMobileLayout) setMobileRightOpen(true)
        }}
      >
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <div className="font-sans text-xl font-semibold text-ink">Position Workspace</div>
          <div className="mt-2 font-mono text-xs text-inkMuted">Select an asset to load its chart and workspace data.</div>
          <button
            type="button"
            onClick={() => openSymbolSearch('')}
            className="mt-5 inline-flex items-center gap-2 rounded-lg border border-border bg-canvas2 px-4 py-2 font-mono text-xs text-ink transition-colors hover:border-ink/40"
            data-testid="position-workspace-neutral-open-symbol"
          >
            <Search size={14} />
            Open symbol
          </button>
        </div>
        <SymbolSearchModal
          open={searchOpen}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          results={symbolSearchResults}
          selectedIndex={searchSelectedIndex}
          onSelectedIndexChange={setSearchSelectedIndex}
          verification={symbolVerification}
          onClose={() => setSearchOpen(false)}
          onSelect={openWorkspaceSearchResult}
          inputRef={searchInputRef}
        />
      </PositionWorkspaceCommandSurface>
    )
  }

  return (
    <PositionWorkspaceCommandSurface
      currentSymbol={primary?.symbol || symbol}
      assetIdeas={assetIdeas}
      className="w-full lg:w-[100vw] lg:relative lg:left-[50%] lg:-ml-[50vw] flex-1 flex flex-col min-h-0 overflow-y-auto lg:overflow-hidden"
      data-testid="position-workspace-chart-lab"
      onShowActions={() => {
        setRightTab('actions')
        if (isMobileLayout) setMobileRightOpen(true)
      }}
      onShowLeftColumn={() => {
        if (isMobileLayout) setMobileLeftOpen(true)
      }}
      onShowRightColumn={() => {
        if (isMobileLayout) setMobileRightOpen(true)
      }}
    >
      {rightTabPreference.status === 'error' && <div role="status" className="fixed bottom-3 right-3 z-50 rounded border border-border bg-canvas px-3 py-2 text-xs text-ink shadow-sm">
        Tab preference could not be saved or loaded. <button className="underline" onClick={rightTabPreference.retry}>Retry</button>
      </div>}
      {(layoutSaveFailed || layoutSaving) && <div role="status" className="fixed bottom-3 left-3 z-50 rounded border border-border bg-canvas px-3 py-2 text-xs text-ink shadow-sm">
        {layoutSaveFailed ? <>Workspace layout could not be saved or loaded. <button className="underline" onClick={() => workspaceLayoutPreferences.filter(preference => preference.status === 'error').forEach(preference => preference.retry())}>Retry</button></> : 'Saving workspace layout…'}
      </div>}
      {!isMobileLayout && (
      <div className="hidden lg:flex flex-col flex-1 min-h-0 px-1 pt-1 pb-0 overflow-y-auto" data-testid="position-workspace-lab-desktop-shell">
        <div ref={desktopColumnsRef} className="relative grid gap-1 flex-1 min-h-0 grid-rows-[auto_minmax(0,1fr)]" style={{ gridTemplateColumns: `${leftWidth}px minmax(0, 1fr) ${rightWidth}px` }}>
        <div className="col-start-1 col-span-3 row-start-1 min-w-0" data-testid="position-workspace-lab-header-wrap">
          <PositionHeader
            ledger ledgerTarget={ledgerTarget}
            positionList
            projects={projects}
            trades={trades}
            executions={executions}
            staticData={staticData}
            isGrouped={projects.length > 1}
            protection={protection}
            wsOrdersReady={scopedOrdersReady}
            liveStopOrders={liveWorkingStopOrders}
            brokerRefreshVersion={brokerRefreshVersion}
            onPlaceStop={() => {
              setRightTab('actions')
              setPlaceStopOpen(true)
            }}
            onPriceUpdate={setCurrentPrice}
            onAccountingUpdate={setActiveAccounting}
            quoteSnapshot={selectedQuote.snapshot}
          />
        </div>

          <aside className="col-start-1 row-start-2 min-w-0 min-h-0" data-testid="position-workspace-lab-left-rail">
            <div className="bg-canvas border border-border rounded-lg overflow-hidden flex flex-col h-full">
              <AssetInfoSplit key={user?.id || 'anonymous'} value={assetInfoHeight.value} onChange={assetInfoHeight.preview} onCommit={assetInfoHeight.commit} card={<AssetInfoCard symbol={selectedSymbol} stage={primary?.id || primary?.__workspaceShell ? primary?.stage : null} display={selectedEquityDisplay} snapshot={selectedQuote.snapshot} data={assetInfo} />}>
              <PositionSwitcher
                currentSymbol={primary?.symbol}
                symbolSearchRequested={assetListSymbolSearchRequested}
                onSymbolSearchRequestHandled={handleAssetListSymbolSearchRequestHandled}
                refreshKey={positionSwitcherRefreshVersion}
                confirmedRestoredIdea={confirmedRestoredIdea}
                onRestoredIdeaPublished={clearConfirmedRestoredIdea}
                workingOrdersApi={workingOrdersApi}
                onOpenSymbol={openEphemeralSymbol}
                onOpenWatchlist={(card) => openEphemeralSymbol(card.symbol)}
                onOpenPosition={(card) => openWorkspaceSearchResult({
                  type: 'workspace',
                  symbol: card.symbol,
                  label: card.symbol,
                  stage: card.stage,
                  projectIds: card.projectIds,
                })}
              />
              </AssetInfoSplit>
            </div>
          </aside>

          <div
            {...getResizeHandleProps('left')}
            className="group absolute row-start-2 top-0 bottom-0 z-20 flex w-2 touch-none cursor-col-resize items-stretch justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
            // Center the unchanged 8px drag target over the 4px panel gap.
            style={{ left: `${leftWidth - 2}px` }}
            data-testid="position-workspace-lab-left-resize-handle"
          >
            <div className={resizeHandleMarkerClassName} />
          </div>

          <div className="col-start-2 row-start-2 min-w-0 min-h-0 flex flex-col gap-1">
            <div className={`${CHART_FRAME_CLASS} relative min-w-0 flex-1 min-h-0`} data-testid="position-workspace-lab-chart-panel">
              {chartHistory.status === 'error' && <div role="status" className="pointer-events-none absolute bottom-2 left-2 right-2 z-30 w-fit max-w-full rounded bg-canvas/95 px-2 py-1 text-xs text-inkMuted">Execution history unavailable. Refresh to retry.</div>}
              {chartHistory.status === 'loading' && <div role="status" className="sr-only">Loading execution history…</div>}
              <WorkspaceCharts
                key={user?.id || 'anonymous'}
                userId={user?.id}
                onOpenSymbol={openEphemeralSymbol}
                toolbarProps={{
                  symbol: symbol || primary.symbol,
                  onSymbolChange: handleSymbolChange,
                  onSymbolSearchOpen: openSymbolSearch,
                  timeframe,
                  onTimeframeChange: handleTimeframeChange,
                  marketSession: effectiveChartMarketSession,
                  onMarketSessionChange: handleMarketSessionChange,
                  extendedHoursEnabled,
                  disabledTimeframes: ['65min'],
                  chartType,
                  onChartTypeChange: handleChartTypeChange,
                  showIndicators: false,
                  positionOverlayEnabled,
                  positionOverlayTitle: 'Position overlay: workspace data',
                  onPositionOverlayToggle: () => setPositionOverlayEnabled(value => !value),
                }}
                chartProps={{
                  symbol: primary.symbol, mode: 'live', timezone: tz,
                  theme: 'workspace', composedChrome: true,
                  markerRangeDefault: 'all', candleAuthority: 'ibkr-runtime',
                  sessionPrice: chartSessionPrice,
                  sessionPriceAuthoritative: !selectedIsOption,
                  sessionPriceSession: chartSession,
                  retainedVwapEnabled: true, quickMeasureEnabled: true,
                }}
                getOverlayProps={getOverlayProps}
              />
            </div>

            <div className="flex-shrink-0" data-testid="position-workspace-lab-timeline-panel">
              {timelineCollapsed ? (
                <button
                  type="button"
                  onClick={() => handleTimelineCollapsedChange(false)}
                  aria-label="Expand decision timeline"
                  className="w-full flex items-center justify-between rounded-lg border border-border bg-canvas px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-inkMuted hover:text-ink transition-colors"
                  data-testid="position-workspace-lab-timeline-toggle"
                >
                  <span>Decision Timeline</span>
                  <ChevronRight size={13} />
                </button>
              ) : (
                <DecisionTimeline
                  tradeIds={tradeIds}
                  symbol={primary.symbol}
                  onNotesCount={setNotesCount}
                  compact
                  onCollapse={() => handleTimelineCollapsedChange(true)}
                />
              )}
            </div>

          </div>

          <div
            {...getResizeHandleProps('right')}
            className="group absolute row-start-2 top-0 bottom-0 z-20 flex w-2 touch-none cursor-col-resize items-stretch justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
            style={{ right: `${rightWidth - 2}px` }}
            data-testid="position-workspace-lab-right-resize-handle"
          >
            <div className={resizeHandleMarkerClassName} />
          </div>

          <WorkspaceColumnKeyboardSurface
            label="Position workspace right column"
            commandTarget="position.right_column.focus"
            tabs={rightTabs}
            activeTab={rightTab}
            onTabChange={setRightTab}
            className="col-start-3 row-start-2 min-w-0 min-h-0 overflow-hidden flex flex-col border border-border rounded-lg bg-canvas"
          >
            <AssetInfoSplit key={user?.id || 'anonymous'} value={ledgerHeight.value} onChange={ledgerHeight.preview} onCommit={ledgerHeight.commit} dividerLabel="Resize position ledger and workspace tools" gap={4} card={<div ref={setLedgerTarget} className="h-full min-h-0" data-testid="position-ledger-target" />}>
            <div className="flex border-b border-border shrink-0 bg-canvas rounded-t-lg" data-testid="position-workspace-lab-right-tabs">
              {rightTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  data-testid={`position-workspace-lab-right-tab-${tab}`}
                  onClick={() => setRightTab(tab)}
                  className={`flex-1 font-mono text-[10px] tracking-normal uppercase px-1.5 py-2.5 -mb-px border-b-2 transition-colors whitespace-nowrap ${
                    rightTab === tab ? 'border-ink text-ink font-semibold' : 'border-transparent text-inkMuted hover:text-ink'
                  }`}
                >
                  {tab === 'notes' && notesCount > 0 ? `Notes (${notesCount})` : rightTabLabel(tab)}
                </button>
              ))}
            </div>

            <div className="flex-1 min-h-0 relative">
              <div data-column-panel-active={rightTab === 'actions' ? 'true' : 'false'} className="absolute inset-0 overflow-y-auto" style={{ display: rightTab === 'actions' ? 'block' : 'none' }}>
                <ActionPanel
                  projects={projects}
                  trades={trades}
                  executions={executions}
                  symbol={primary?.symbol}
                  isActive={isActive}
                  onPositionRefresh={handlePositionRefresh}
                  onStopMoved={handleStopMoved}
                  protection={protection}
                  placeStopOpen={placeStopOpen}
                  placeStopPrice={placeStopPrice}
                  setPlaceStopPrice={setPlaceStopPrice}
                  onPlaceStopConfirm={handlePlaceStopConfirm}
                  onPlaceStopCancel={() => { setPlaceStopOpen(false); setPlaceStopPrice('') }}
                  onPlaceStop={() => setPlaceStopOpen(true)}
                  currentPrice={currentPrice}
                  onStopSelectionChange={setActiveStopPrice}
                  workingOrdersApi={workingOrdersApi}
                />
              </div>

              <div data-column-panel-active={rightTab === 'orders' ? 'true' : 'false'} className="absolute inset-0 overflow-y-auto" style={{ display: rightTab === 'orders' ? 'block' : 'none' }}>
                <WorkingOrdersPanel
                  projects={projects}
                  trades={trades}
                  symbol={primary?.symbol}
                  accountNumbers={workspaceAccountNumbers}
                  onPositionRefresh={handlePositionRefresh}
                  liveStopPrice={liveStopPrice}
                  portfolioValue={portfolioValue}
                  workingOrdersApi={workingOrdersApi}
                />
              </div>

              <div data-column-panel-active={rightTab === 'executions' ? 'true' : 'false'} className="absolute inset-0 overflow-y-auto" style={{ display: rightTab === 'executions' ? 'block' : 'none' }}>
                <div className="p-3">
                  <TabContent
                    activeTab="Executions"
                    primary={primary}
                    executions={executions}
                    tradeIds={tradeIds}
                    notesCount={notesCount}
                    tz={tz}
                  />
                </div>
              </div>

              <div data-column-panel-active={rightTab === 'notes' ? 'true' : 'false'} className="absolute inset-0 overflow-y-auto" style={{ display: rightTab === 'notes' ? 'block' : 'none' }}>
                <div className="p-3">
                  <TabContent
                    activeTab="Notes"
                    primary={primary}
                    executions={executions}
                    tradeIds={tradeIds}
                    notesCount={notesCount}
                    tz={tz}
                  />
                </div>
              </div>

              <div data-column-panel-active={rightTab === 'setups' ? 'true' : 'false'} className="absolute inset-0 overflow-y-auto" style={{ display: rightTab === 'setups' ? 'block' : 'none' }}>
                  <div className="p-3 space-y-3">
                      <>
                        {setupProjects.map((setupProject) => {
                          const setupClosed = ['closed', 'reviewed'].includes(String(setupProject.stage || '').toLowerCase())
                          const setupNeedsSending = ['idea', 'planned'].includes(String(setupProject.stage || '').toLowerCase())
                          return (
                            <div key={setupProject.id || `${setupProject.symbol}-${setupProject.created_at}`}>
                              <WorkingSetupCard
                                brokerEntryStatus={setupEntryStatuses.get(setupProject.id)}
                                project={setupClosed ? { ...setupProject, setup_status: 'closed' } : setupProject}
                                symbol={setupProject.symbol.toUpperCase()}
                                readOnly={setupClosed}
                                stageForSending={setupNeedsSending}
                                onSelect={() => selectSetupCard(setupProject)}
                                onPositionRefresh={handlePositionRefresh}
                                cancellationPending={cancelingSetupIds.has(String(setupProject.id))}
                                onCancellationPendingChange={handleCancellationPendingChange}
                                onMetadataChanged={() => {
                                  setSelectedSetupId(null)
                                  setManualSetupDraft(null)
                                  setPreviewSetupLines(null)
                                }}
                                repairRequested={searchParams.get('repairSetup') === 'missing'}
                              />
                              {activeCloseReview && selectedSetupProject?.id === setupProject.id && (
                                <SetupEditReview review={activeCloseReview} onKeepEditing={() => setSetupCloseReview(null)} />
                              )}
                            </div>
                          )
                        })}

                        {selectedSetupProject && !manualSetupDraft && (
                          <div hidden={!!activeCloseReview}>
                            <SetupStagingPanel
                              key={`setup:${selectedSetupProject.symbol}:${selectedSetupProject.id}`}
                              editorRef={setupEditorRef}
                              onCloseReview={setSetupCloseReview}
                              project={selectedSetupProject}
                              setupData={{
                                pattern: selectedSetupProject.setup_pattern || '',
                                entryType: selectedSetupProject.entry_type || 'Pullback',
                                triggerPrice: selectedSetupProject.trigger_price || '',
                                stopPrice: selectedSetupProject.pattern_failure_price || '',
                                targetPrice: selectedSetupProject.sell_target_price || '',
                                sizePct: selectedSetupProject.recommended_size_pct || 9,
                              }}
                              symbol={selectedSetupProject.symbol}
                              referencePrice={currentPrice}
                              isAddTranche={String(selectedSetupProject.stage || '').toLowerCase() === 'active'}
                              isManual={false}
                              onClearSetup={['idea', 'planned'].includes(selectedSetupProject.stage) ? () => cancelSavedIdea(selectedSetupProject) : undefined}
                              onCancel={closeSelectedSetup}
                              onSend={closeSelectedSetup}
                              onSave={closeSelectedSetup}
                              onPreviewUpdate={setPreviewSetupLines}
                              onPositionRefresh={handlePositionRefresh}
                              onRepairMissingOrders={handleImmediateSetupRepair}
                              isEphemeral={isEphemeral}
                              insertEphemeralProject={insertEphemeralProject}
                              onEphemeralFinalized={onEphemeralFinalized}
                            />
                          </div>
                        )}

                        {manualSetupDraft && (
                          <SetupStagingPanel
                            key={`manual:${primary?.symbol}`}
                            project={manualSetupProject}
                            setupData={manualSetupDraft}
                            symbol={primary?.symbol}
                            referencePrice={currentPrice}
                            isAddTranche={isActive}
                            isManual
                            onCancel={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                            onSend={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                            onSave={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                            onPreviewUpdate={setPreviewSetupLines}
                            onPositionRefresh={handlePositionRefresh}
                            onRepairMissingOrders={handleImmediateSetupRepair}
                            isEphemeral={isEphemeral || manualSetupCreatesFreshProject}
                            insertEphemeralProject={insertEphemeralProject}
                            onEphemeralFinalized={onEphemeralFinalized}
                          />
                        )}

                        {!manualSetupDraft && !selectedSetupProject && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedSetupId(null)
                              setPreviewSetupLines(null)
                              setManualSetupDraft({ ownerSymbol: primary?.symbol, ownerProjectId: primary?.id || null, pattern: '', entryType: 'Pullback', triggerPrice: '', stopPrice: '', targetPrice: '', sizePct: 9 })
                            }}
                            className="w-full font-mono text-[10px] px-3 py-3 rounded-lg border border-dashed border-border text-inkMuted hover:text-ink hover:border-ink/30 transition-colors flex items-center justify-center gap-1"
                          >
                            <Plus size={10} />
                            New Setup
                          </button>
                        )}
                        {!selectedSetupProject && !manualSetupDraft && <SetupHistory projects={retiredSetups} error={setupHistoryError} onRestore={restoreHistoricalSetup} />}
                      </>
                  </div>
                </div>
            </div>
            </AssetInfoSplit>
          </WorkspaceColumnKeyboardSurface>
        </div>
      </div>
      )}

      {isMobileLayout && (
        // CE-04g-UI-mobile-workspace-followup — fixed-viewport mobile shell.
        // `h-[100dvh]` + `overflow-hidden` make mobile behave like the
        // desktop workspace: no vertical page scroll. Header and chart are
        // flex children; the chart flexes to whatever height remains.
        <div className="lg:hidden h-[100dvh] px-1 pt-1.5 pb-2 flex flex-col gap-2 overflow-hidden" data-testid="position-workspace-lab-mobile-shell">
          <button
            type="button"
            onClick={() => setMobileLeftOpen(true)}
            className="fixed left-0 top-1/2 z-30 flex h-14 w-6 -translate-y-1/2 items-center justify-center rounded-r-lg border border-l-0 border-border bg-canvas/90 text-inkMuted shadow-sm backdrop-blur-sm"
            aria-label="Open positions"
          >
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => setMobileRightOpen(true)}
            className="fixed right-0 top-1/2 z-30 flex h-14 w-6 -translate-y-1/2 items-center justify-center rounded-l-lg border border-r-0 border-border bg-canvas/90 text-inkMuted shadow-sm backdrop-blur-sm"
            aria-label="Open workspace tools"
          >
            <ChevronLeft size={14} />
          </button>

          {mobileLeftOpen && (
            <div className="fixed inset-0 z-40 flex" data-testid="position-workspace-lab-mobile-left-drawer">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileLeftOpen(false)} />
              <div className="relative flex w-[280px] max-w-[84vw] flex-col border-r border-border bg-canvas shadow-xl">
                <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Positions</span>
                  <button type="button" onClick={() => setMobileLeftOpen(false)} className="p-1 text-inkMuted hover:text-ink" aria-label="Close positions">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <AssetInfoSplit key={user?.id || 'anonymous'} value={assetInfoHeight.value} onChange={assetInfoHeight.preview} onCommit={assetInfoHeight.commit} card={<AssetInfoCard symbol={selectedSymbol} stage={primary?.id || primary?.__workspaceShell ? primary?.stage : null} display={selectedEquityDisplay} snapshot={selectedQuote.snapshot} data={assetInfo} />}>
                  <PositionSwitcher
                    currentSymbol={primary?.symbol}
                    symbolSearchRequested={assetListSymbolSearchRequested}
                    onSymbolSearchRequestHandled={handleAssetListSymbolSearchRequestHandled}
                    refreshKey={positionSwitcherRefreshVersion}
                    confirmedRestoredIdea={confirmedRestoredIdea}
                    onRestoredIdeaPublished={clearConfirmedRestoredIdea}
                    workingOrdersApi={workingOrdersApi}
                    onOpenSymbol={(nextSymbol) => {
                      setMobileLeftOpen(false)
                      openEphemeralSymbol(nextSymbol)
                    }}
                    onOpenWatchlist={(card) => {
                      setMobileLeftOpen(false)
                      openEphemeralSymbol(card.symbol)
                    }}
                    onOpenPosition={(card) => {
                      setMobileLeftOpen(false)
                      openWorkspaceSearchResult({
                        type: 'workspace',
                        symbol: card.symbol,
                        label: card.symbol,
                        stage: card.stage,
                        projectIds: card.projectIds,
                      })
                    }}
                  />
                  </AssetInfoSplit>
                </div>
              </div>
            </div>
          )}

          {mobileRightOpen && (
            <div className="fixed inset-0 z-40 flex justify-end" data-testid="position-workspace-lab-mobile-right-drawer">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileRightOpen(false)} />
              <WorkspaceColumnKeyboardSurface
                label="Position workspace right column"
                commandTarget="position.right_column.focus"
                tabs={rightTabs}
                activeTab={rightTab}
                onTabChange={setRightTab}
                className="relative flex w-[330px] max-w-[88vw] flex-col border-l border-border bg-canvas shadow-xl"
              >
                <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Workspace</span>
                  <button type="button" onClick={() => setMobileRightOpen(false)} className="p-1 text-inkMuted hover:text-ink" aria-label="Close workspace tools">
                    <X size={16} />
                  </button>
                </div>
                <div ref={setLedgerTarget} className="h-[35vh] min-h-0 overflow-hidden shrink-0" data-testid="position-ledger-target" />
                <div className="flex border-b border-border">
                  {rightTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setRightTab(tab)}
                      className={`flex-1 px-1 py-2 font-mono text-[9px] uppercase transition-colors ${
                        rightTab === tab ? 'text-ink font-semibold' : 'text-inkMuted'
                      }`}
                    >
                      {rightTabLabel(tab)}
                    </button>
                  ))}
                </div>
                <div data-column-panel-active="true" className="flex-1 overflow-y-auto p-3">
                  {rightTab === 'actions' && (
                    <ActionPanel
                      projects={projects}
                      trades={trades}
                      executions={executions}
                      symbol={primary?.symbol}
                      isActive={isActive}
                      onPositionRefresh={handlePositionRefresh}
                      onStopMoved={handleStopMoved}
                      protection={protection}
                      placeStopOpen={placeStopOpen}
                      placeStopPrice={placeStopPrice}
                      setPlaceStopPrice={setPlaceStopPrice}
                      onPlaceStopConfirm={handlePlaceStopConfirm}
                      onPlaceStopCancel={() => { setPlaceStopOpen(false); setPlaceStopPrice('') }}
                      onPlaceStop={() => setPlaceStopOpen(true)}
                      currentPrice={currentPrice}
                      onStopSelectionChange={setActiveStopPrice}
                      workingOrdersApi={workingOrdersApi}
                    />
                  )}
                  {rightTab === 'orders' && (
                    <WorkingOrdersPanel
                      projects={projects}
                      trades={trades}
                      symbol={primary?.symbol}
                      accountNumbers={workspaceAccountNumbers}
                      onPositionRefresh={handlePositionRefresh}
                      liveStopPrice={liveStopPrice}
                      portfolioValue={portfolioValue}
                      workingOrdersApi={workingOrdersApi}
                    />
                  )}
                  {rightTab === 'executions' && (
                    <TabContent
                      activeTab="Executions"
                      primary={primary}
                      executions={executions}
                      tradeIds={tradeIds}
                      notesCount={notesCount}
                      tz={tz}
                    />
                  )}
                  {rightTab === 'notes' && (
                    <TabContent
                      activeTab="Notes"
                      primary={primary}
                      executions={executions}
                      tradeIds={tradeIds}
                      notesCount={notesCount}
                      tz={tz}
                    />
                  )}
                  {rightTab === 'setups' && (
                    <div className="space-y-3">
                        <>
                          {setupProjects.map((setupProject) => {
                            const setupClosed = ['closed', 'reviewed'].includes(String(setupProject.stage || '').toLowerCase())
                            const setupNeedsSending = ['idea', 'planned'].includes(String(setupProject.stage || '').toLowerCase())
                            return (
                              <div key={setupProject.id || `${setupProject.symbol}-${setupProject.created_at}`}>
                                <WorkingSetupCard
                                  brokerEntryStatus={setupEntryStatuses.get(setupProject.id)}
                                  project={setupClosed ? { ...setupProject, setup_status: 'closed' } : setupProject}
                                  symbol={setupProject.symbol.toUpperCase()}
                                  readOnly={setupClosed}
                                  stageForSending={setupNeedsSending}
                                  onSelect={() => selectSetupCard(setupProject)}
                                  onPositionRefresh={handlePositionRefresh}
                                  cancellationPending={cancelingSetupIds.has(String(setupProject.id))}
                                  onCancellationPendingChange={handleCancellationPendingChange}
                                  onMetadataChanged={() => {
                                    setSelectedSetupId(null)
                                    setManualSetupDraft(null)
                                    setPreviewSetupLines(null)
                                  }}
                                  repairRequested={searchParams.get('repairSetup') === 'missing'}
                                />
                                {activeCloseReview && selectedSetupProject?.id === setupProject.id && (
                                  <SetupEditReview review={activeCloseReview} onKeepEditing={() => setSetupCloseReview(null)} />
                                )}
                              </div>
                            )
                          })}
                          {selectedSetupProject && !manualSetupDraft && (
                            <div hidden={!!activeCloseReview}>
                              <SetupStagingPanel
                                key={`setup:${selectedSetupProject.symbol}:${selectedSetupProject.id}`}
                                editorRef={setupEditorRef}
                                onCloseReview={setSetupCloseReview}
                                project={selectedSetupProject}
                                setupData={{
                                  pattern: selectedSetupProject.setup_pattern || '',
                                  entryType: selectedSetupProject.entry_type || 'Pullback',
                                  triggerPrice: selectedSetupProject.trigger_price || '',
                                  stopPrice: selectedSetupProject.pattern_failure_price || '',
                                  targetPrice: selectedSetupProject.sell_target_price || '',
                                  sizePct: selectedSetupProject.recommended_size_pct || 9,
                                }}
                                symbol={selectedSetupProject.symbol}
                                referencePrice={currentPrice}
                                isAddTranche={String(selectedSetupProject.stage || '').toLowerCase() === 'active'}
                                isManual={false}
                                onClearSetup={['idea', 'planned'].includes(selectedSetupProject.stage) ? () => cancelSavedIdea(selectedSetupProject) : undefined}
                                onCancel={closeSelectedSetup}
                                onSend={closeSelectedSetup}
                                onSave={closeSelectedSetup}
                                onPreviewUpdate={setPreviewSetupLines}
                                onPositionRefresh={handlePositionRefresh}
                                onRepairMissingOrders={handleImmediateSetupRepair}
                                isEphemeral={isEphemeral}
                                insertEphemeralProject={insertEphemeralProject}
                                onEphemeralFinalized={onEphemeralFinalized}
                              />
                            </div>
                          )}
                          {manualSetupDraft && (
                            <SetupStagingPanel
                              key={`manual:${primary?.symbol}`}
                              project={manualSetupProject}
                              setupData={manualSetupDraft}
                              symbol={primary?.symbol}
                              referencePrice={currentPrice}
                              isAddTranche={isActive}
                              isManual
                              onCancel={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                              onSend={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                              onSave={() => { setSelectedSetupId(null); setManualSetupDraft(null); setPreviewSetupLines(null) }}
                              onPreviewUpdate={setPreviewSetupLines}
                              onPositionRefresh={handlePositionRefresh}
                              onRepairMissingOrders={handleImmediateSetupRepair}
                              isEphemeral={isEphemeral || manualSetupCreatesFreshProject}
                              insertEphemeralProject={insertEphemeralProject}
                              onEphemeralFinalized={onEphemeralFinalized}
                            />
                          )}
                          {!manualSetupDraft && !selectedSetupProject && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSetupId(null)
                                setPreviewSetupLines(null)
                                setManualSetupDraft({ ownerSymbol: primary?.symbol, ownerProjectId: primary?.id || null, pattern: '', entryType: 'Pullback', triggerPrice: '', stopPrice: '', targetPrice: '', sizePct: 9 })
                              }}
                              className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-border px-3 py-3 font-mono text-[10px] text-inkMuted"
                            >
                              <Plus size={10} />
                              New Setup
                            </button>
                          )}
                            {!selectedSetupProject && !manualSetupDraft && <SetupHistory projects={retiredSetups} error={setupHistoryError} onRestore={restoreHistoricalSetup} />}
                        </>
                    </div>
                  )}
                </div>
              </WorkspaceColumnKeyboardSurface>
            </div>
          )}

          <PositionHeader
            ledger ledgerTarget={ledgerTarget}
            positionList
            projects={projects}
            trades={trades}
            executions={executions}
            staticData={staticData}
            isGrouped={projects.length > 1}
            protection={protection}
            wsOrdersReady={scopedOrdersReady}
            liveStopOrders={liveWorkingStopOrders}
            brokerRefreshVersion={brokerRefreshVersion}
            onPlaceStop={() => {
              setRightTab('actions')
              setPlaceStopOpen(true)
              setMobileRightOpen(true)
            }}
            onPriceUpdate={setCurrentPrice}
            onAccountingUpdate={setActiveAccounting}
            quoteSnapshot={selectedQuote.snapshot}
          />

          {/* CE-04g-UI-mobile-workspace-followup — the chart panel flexes to
              the space left under the header instead of forcing page scroll
              with a fixed 78vh height. `min-h-0` lets the flex child shrink
              below its content height so the shell never overflows. */}
          <div className={`${CHART_FRAME_CLASS} relative flex-1 min-h-0`} data-testid="position-workspace-lab-mobile-chart-panel">
              {chartHistory.status === 'error' && <div role="status" className="pointer-events-none absolute bottom-2 left-2 right-2 z-30 w-fit max-w-full rounded bg-canvas/95 px-2 py-1 text-xs text-inkMuted">Execution history unavailable. Refresh to retry.</div>}
              {chartHistory.status === 'loading' && <div role="status" className="sr-only">Loading execution history…</div>}
            <WorkspaceCharts
                key={user?.id || 'anonymous'}
                userId={user?.id}
                onOpenSymbol={openEphemeralSymbol}
                toolbarProps={{
                  symbol: symbol || primary.symbol,
                  onSymbolChange: handleSymbolChange,
                  onSymbolSearchOpen: openSymbolSearch,
                  timeframe,
                  onTimeframeChange: handleTimeframeChange,
                  marketSession: effectiveChartMarketSession,
                  onMarketSessionChange: handleMarketSessionChange,
                  extendedHoursEnabled,
                  disabledTimeframes: ['65min'],
                  chartType,
                  onChartTypeChange: handleChartTypeChange,
                  showIndicators: false,
                  positionOverlayEnabled,
                  positionOverlayTitle: 'Position overlay: workspace data',
                  onPositionOverlayToggle: () => setPositionOverlayEnabled(value => !value),
                }}
                chartProps={{
                  symbol: primary.symbol, mode: 'live', timezone: tz,
                  theme: 'workspace', composedChrome: true,
                  markerRangeDefault: 'all', candleAuthority: 'ibkr-runtime',
                  sessionPrice: chartSessionPrice,
                  sessionPriceAuthoritative: !selectedIsOption,
                  sessionPriceSession: chartSession,
                  retainedVwapEnabled: true, quickMeasureEnabled: true,
                }}
                getOverlayProps={getOverlayProps}
              />
          </div>

          {/* CE-04g-UI-mobile-workspace-followup — the below-chart quick
              actions/tabs panel is gone from the default mobile layout.
              Actions / Orders / Executions / Notes / Setups remain reachable
              through the right drawer (`mobileRightOpen`), which `Place Stop`
              already opens. */}
        </div>
      )}

      <OrderConfirmationModal
        open={confirmState.open}
        onClose={cancelConfirmation}
        onConfirm={confirmOrder}
        loading={confirmState.loading}
        action={confirmState.action}
        symbol={confirmState.symbol}
        orders={confirmState.orders}
        results={confirmState.results}
      />
      <SymbolSearchModal
        open={searchOpen}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        results={symbolSearchResults}
        selectedIndex={searchSelectedIndex}
        onSelectedIndexChange={setSearchSelectedIndex}
        verification={symbolVerification}
        onClose={() => setSearchOpen(false)}
        onSelect={openWorkspaceSearchResult}
        inputRef={searchInputRef}
      />
    </PositionWorkspaceCommandSurface>
  )
}
